/**
 * itemsAdapter.ts — Bridges the Peanut Percs + Buncha Bullshiiii systems
 * to the engine's ItemsModule interface.
 *
 * Responsibilities:
 * - Level-up: generates 3 perc options, pauses game, waits for player choice
 * - Chests: show 3 item choices, player picks one
 * - Shrines: show 3 stackable stat buff choices, player picks one
 * - Stat application: apply perc + item + shrine bonuses to player in real-time
 */
import type { GameState, ViewBounds, ItemsModule, UpgradeChoice, Player, PlayerId } from './types';
// BETA 20.72 — Silly Juice (R15) stack VFX needs the shared particle spawner
import { spawnParticles } from './types';
import {
  type PeanutPercRunState,
  type LevelUpOptionDisplay,
  type PercSlotDisplay,
  createPercRunState,
  addXp,
  generateLevelUpOptions,
  selectLevelUpOption,
  calculatePercRunBonuses,
  applyPercBonusesToStats,
  getPercSlotDisplays,
  getLevelUpOptionDisplays,
  getXpBarDisplay,
} from './peanutPercSystem';
// BETA 19.2 — rollUpgradeRarity (in peanutPercSystem) for weapon card
// rarity rolling. UpgradeRarity type lives in peanutPercs (the data file).
import { rollUpgradeRarity } from './peanutPercSystem';
// BETA 22.87 — weapon cards ride the LEVEL-UP card system's 4-tier rarity
// (perkCards.ts). Shrines + chests keep rollUpgradeRarity's 3-tier.
import { rollCardRarity } from './perkCards';
import { WILD_CARD_BY_ITEM, wildCardArtPath, WILD_CARD_TIER_STACKS } from './wildCards';   // BETA 22.89
import { type UpgradeRarity, luckValueMult, rollBuffJitter } from './peanutPercs';
import { rollRng } from './rng'; // BETA 20.93 — seeded balance rolls (reproducible shrine/chest/weapon offers)
import {
  type BullshiiiiSystemState,
  type InventoryDisplay,
  createBullshiiiiSystem,
  addBullshiiii,
  rollChestItem,
  recalculateStats,
  getInventoryDisplay,
  rollItemDimension,
  _wasRecentlyPicked,   // BETA 20.03 — cross-chest dedup for _pickChestItems
  _recordChestPick,     // BETA 20.03 — cross-chest dedup for _pickChestItems
  clearRecentChestPicks, // BETA 20.06 — reset dedup buffer on new run
  isItemAvailableForPickup, // BETA 21.86 — drop owned-at-max-stacks items from chest offers
} from './bunchaBullshiiiiSystem';
import { BUNCHA_BULLSHIIII, type BullshiiiiItem, type ItemRarity, type BuffDimension, RARITY_COLORS as ITEM_RARITY_COLORS_BS, getActiveBullshiiii, tuneOf } from './bunchaBullshiiii';
import { PEANUT_PERCS, type PercStat } from './peanutPercs';
import { playLevelUp, playCardHover } from './gameSfx';
// BETA 16.12 — End the muffled duck after shrine/chest/level-up
// selection or skip. Was: resumeBgm (because we paused it). Now:
// endMuffledDuck (we ducked + filtered it). Per Dylan: "main game
// audio... still playin and not stopped or paused. just under."
import { endMuffledDuck } from './audio';
// BETA 19.9 — focus-screen cleanup at the SOURCE of each pick so
// bot-driven paths (autoPlaytester) get the same cleanup as the engine's
// inline _pickCh/_pickShrine/_pickLevelUp helpers. Was: only the engine
// handler cleaned activeScreen; bot called handle*Selection directly →
// activeScreen lingered → ACTIVE_SCREEN_LINGERING anomaly fired on
// every chest/shrine/levelup pick in bot mode.
import { unregisterFocusScreen, setActiveScreen, getActiveScreen } from './gamepadFocus';
import { WEAPONS, getActivePercs, getActiveBullshiiiiItems, getActiveWeapons, ALWAYS_ON_BULLSHIIII_IDS } from './loadoutSelection';
import { spawnWeightOf } from './balanceOverrides';
import { getWeaponSystemState, addWeaponById, levelUpWeaponById } from './weaponAdapter';
import { getWeaponDef, computeWeaponStats, getWeaponStatPool, luckMagnitudeMult, type WeaponStatKey } from './weapons';
// BETA 22.47 — level-up cards should show the player's MODDED combo sprite, not base art.
import { isArmed } from './weaponArmed';
import { equippedCodes } from './weaponModLoadout';
import { modComboImage } from './weaponMods';

/** If `weaponId` is armed with mods, return its kitted combo sprite so level-up
 *  cards match the gun in your run (Dylan: "i dont want to see the basic version
 *  of my moded gun"). Falls back to `baseUrl` for unmodded / non-revolver guns. */
function _moddedCardIcon(weaponId: string, baseUrl: string): string {
  try {
    if (isArmed(weaponId)) {
      const c = equippedCodes(weaponId);
      if (c.length) { const u = modComboImage(weaponId, c); if (u) return u; }
    }
  } catch { /* mod resolver not ready */ }
  return baseUrl;
}
// BETA 15.26 — character passive system (Layer 1 + Layer 2)
import { applyCharacterPassive } from './characterPassives';
// BETA 15.34 — pick audit log (for weapon level-up + add-weapon entries)
import { recordPick } from './pickAudit';
import { getCurrentRunTime } from './weaponLab';
// BETA 16.20 — PSBOT cycle-23 P1.5: live-play per-pick event logger.
// Called from level-up / chest / shrine pick paths so PSBOT RUN can
// see which items/perks players actually picked, at what time, source,
// and rarity. Was previously empty in Supabase rows because this hook
// didn't exist.
import { recordLivePickEvent } from './livePlayTracker';

// ─── Bot mode flag (set externally to unlock all percs/items for the bot) ──
let _botModeUnlockAll = false;
export function setBotModeUnlockAll(active: boolean): void { _botModeUnlockAll = active; }

// ─── Silly Juice kill-stack tracker ─────────────────────────────────────────
// Call recordItemKill() from weaponAdapter on every enemy killed.
// Call resetSillyJuiceOnDamage() from enemyAdapter when player takes damage.
// (Stack counters live in the per-player ItemsRunState store `S` — see the
// Internal State section below. These hooks act on the ACTIVE player.)
// BETA 20.72 — VFX state stash. recordItemKill/resetSillyJuiceOnDamage are
// bare module functions with no GameState param (callers live in
// weaponAdapter/enemyAdapter), so the adapter's per-frame update() parks the
// current state here purely so those triggers can reach state.particles.
// VISUALS ONLY — never read for gameplay.
let _vfxStateRef: GameState | null = null;
// BETA 17.15 — PSBOT cycle-30af: Silly Juice max 25 stacks × +5% = +125%
// damage was trivial to reach + stacked with Mystery Meat + N05 for
// Cowboy-floor-2 invincibility. Reworked: max stacks bumped 25 → 50
// (per stack-multiplier) but per-stack damage 5% → 2.5%. Net max bonus
// same (+125% at 50 stacks per stack-source), but takes 2× the kills
// to max so it doesn't trivialize. Also pairs with MMS taper above.
export function recordItemKill(): void {
  if (!S.itemState) return;
  const sjSlot = S.itemState.inventory.find(s => s.item.id === 'R15');
  if (!sjSlot) return;
  const _before = S._sillyJuiceKillStacks; // BETA 20.72 — detect a REAL gain (not at-cap no-op)
  S._sillyJuiceKillStacks = Math.min(tuneOf('R15', 'capPerCopy', 50) * sjSlot.stacks, S._sillyJuiceKillStacks + sjSlot.stacks);  // BETA 21.77 — editable cap
  if (S._sillyJuiceKillStacks > S._sillyJuicePeakStack) S._sillyJuicePeakStack = S._sillyJuiceKillStacks;
  // BETA 20.72 — Silly Juice (R15 item) fills up: same orange rising-mote
  // identity as the Mr Silly character passive (it IS the same juice), but
  // throttled to 1 spawn per 3 gains — R15 stacks on EVERY kill up to
  // 50×stacks, so per-gain motes would be solid spam mid-horde.
  if (S._sillyJuiceKillStacks > _before && _vfxStateRef) {
    S._sillyJuiceVfxGains++;
    if (S._sillyJuiceVfxGains >= 3) {
      S._sillyJuiceVfxGains = 0;
      spawnParticles(_vfxStateRef.particles, _vfxStateRef.player.x, _vfxStateRef.player.y, '#ffaa33', 2, {
        shape: 'mote', speedMin: 10, speedMax: 40, gravity: -90,
        lifeMin: 0.45, lifeMax: 0.8, sizeMin: 2, sizeMax: 4,
      });
    }
  }
}
export function resetSillyJuiceOnDamage(): void {
  // BETA 20.72 — juice DUMPED (the R15 item still zeroes fully, unlike the
  // character passive's 19.62 halve): red spark flash when stacks were
  // actually lost. VFX gated to the playing phase only — the reset itself
  // is untouched (visuals-only rule).
  if (S._sillyJuiceKillStacks > 0 && _vfxStateRef && _vfxStateRef.phase === 'playing') {
    spawnParticles(_vfxStateRef.particles, _vfxStateRef.player.x, _vfxStateRef.player.y, '#ff3322', 7, {
      shape: 'spark', speedMin: 160, speedMax: 300,
      lifeMin: 0.18, lifeMax: 0.32, stretch: 5, fadePow: 1.5,
    });
  }
  S._sillyJuiceKillStacks = 0;
}
export function getSillyJuiceStacks(): number { return S._sillyJuiceKillStacks; }
// BETA 17.08 — peak getter + resetter for telemetry surfacing.
export function getSillyJuicePeakStack(): number { return S._sillyJuicePeakStack; }
export function resetSillyJuicePeakStack(): void { S._sillyJuicePeakStack = 0; }

// ─── Internal State ─────────────────────────────────────────────────
// BETA 23.66 — CO-OP PHASE 1 part 2: what used to be ~35 module-level `let`s
// (perc/item run state + every level-up / chest / shrine choice-session
// singleton) is now ONE ItemsRunState store per player — the exact same
// conversion characterPassives.ts got in 23.62. `S` is the ACTIVE player's
// store; every read/write below goes through it with the same values and
// the same lifecycles as the old singletons. Single-player never switches
// stores (always p1), so behavior is identical. In co-op the engine calls
// setActiveItemsAdapterPlayer(id) before running a player's frame slice.
//
// NOT in the store (per-variable judgment, documented for Phase 2):
//   • _botModeUnlockAll — bot-harness flag, machine-global.
//   • _vfxStateRef — parked GameState ref (one sim per machine, VFX only).
//   • _shrineFloor / _shrineRunMinutes — WORLD context (floor + run clock are
//     shared by every player in co-op).
//   • _shrineSelectTracker — engine-registered telemetry callback.
//   • _crossRunOfferedWeapons — CROSS-RUN variety memory for THIS machine's
//     human, deliberately never reset (BETA 22.05); a remote player's prior
//     runs happened on their own machine, so it cannot be per-player here.
// DELETED as dead code (declared, never read anywhere): the old
//   `pendingWeaponUpgradeCards` array and legacy `shrineOptions` var.
interface ItemsRunState {
  // Silly Juice (R15 item) kill stacks — recordItemKill/resetSillyJuiceOnDamage.
  _sillyJuiceKillStacks: number;
  // BETA 20.72 — R15 mote-VFX throttle: 1 spawn per 3 actual stack gains.
  _sillyJuiceVfxGains: number;
  // BETA 17.08 — PSBOT cycle-30t Tier C #8: peak stack value reached this
  // run. Reset at run start via resetSillyJuicePeakStack(), NOT by init.
  _sillyJuicePeakStack: number;

  // The two big run-state systems (Peanut Percs + Buncha Bullshiiii items).
  percState: PeanutPercRunState | null;
  itemState: BullshiiiiSystemState | null;

  // Level-up choice session.
  pendingLevelUp: boolean;
  levelUpSelectedIndex: number;  // -1 = nothing pre-selected; hover or press 1/2/3
  levelUpHoverIndex: number;     // -1 = no hover
  // Weapon offer injected into card slot 2 at certain levels.
  pendingWeaponCard: LevelUpOptionDisplay | null;
  // BETA 16.98 — every perc-name + weapon-id offered THIS level-up session
  // (across all rerolls) so a reroll never repeats an offer.
  _levelUpSessionOfferedWeapons: Set<string>;
  // BETA 19.71 — last 1-2 weapons that received a stat upgrade card
  // (cross-level-up dedup; reset per run by init).
  _recentUpgradeWeaponIds: string[];
  // BETA 19.2/19.4/19.14/21.02 — per-card OFFER-TIME stash (rarity / rolled
  // stat subset / luck-magnitude mult / buff jitter), keyed by weaponId, so
  // the apply path commits EXACTLY the card the player saw. Cleared on pick,
  // reroll, and clearPendingLevelUp.
  _pendingWeaponCardRarities: Map<string, UpgradeRarity>;
  _pendingWeaponCardStats: Map<string, WeaponStatKey[]>;
  _pendingWeaponCardLuckMults: Map<string, number>;
  _pendingWeaponCardJitters: Map<string, number>;
  // Cached level-up display — computed ONCE when level-up starts, stable
  // every frame (prevents rollRng() flicker).
  cachedLevelUpDisplay: LevelUpOptionDisplay[] | null;

  // Shrine choice session.
  pendingShrineChoice: boolean;
  shrineSelectedIndex: number;
  shrineHoverIndex: number;
  // Every shrine/chest buff applied this run — applyAllBonusesToPlayer
  // recomposes stats from this list each frame.
  shrineAppliedBuffs: Array<{ stat: string; value: number; pct: boolean; label: string }>;
  pendingShrineBuffOptions: ShrineBuff[] | null;
  shrineIsGolden: boolean;
  // BETA 20.77 — luck at the moment the shrine was activated (reroll reuse).
  _lastShrineLuck: number;
  // BETA 16.33 — cumulative `+N% Difficulty` shrine total; recomputed each
  // frame from shrineAppliedBuffs (see the BETA 19.70 runaway-counter fix).
  _shrineDifficultyAccumPct: number;
  // BETA 16.21 — anti-repeat queue of the last 18 OFFERED shrine buff ids.
  // Reset per run via resetShrineOfferQueue(), NOT by init.
  _recentlyOfferedShrineBuffs: string[];

  // Chest spin + choice session.
  _chestSpin: ChestSpinState | null;
  // BETA 20.77 — player luck at chest-open time (set by startChestSpin; read
  // in _pickChestItems; persists for the reroll path).
  _chestLuck: number;
  pendingChestItems: BullshiiiiItem[];
  // BETA 16.29 — parallel array of rolled dimensions per chest pick (only for
  // OWNED items with buffDimensions); committed to slot.pickedDimensions on pickup.
  pendingChestDimensions: (BuffDimension | undefined)[];
  // BETA 21.00 — parallel per-pick JITTER factors rolled ONCE at offer time so
  // preview == granted (no re-roll). Cleared wherever pendingChestItems is.
  pendingChestJitters: number[];
  // BETA 22.89 — WILD CARDS: per-offer rolled TIER (card art shell + stack mult ×1/2/3/5).
  pendingChestTiers: UpgradeRarity[];
  chestHoverIndex: number;
  chestIsGold: boolean;
  chestIsElite: boolean;  // BETA 20.49 — Blue Elite Chest flag
  // BETA 16.98 — items offered across THIS chest's open + rerolls (a reroll
  // can't repeat the set). Cleared on pick or skip.
  _chestSessionOfferedIds: Set<string>;
}

// Initial values = exactly the old module-let initializers.
function freshItemsRunState(): ItemsRunState {
  return {
    _sillyJuiceKillStacks: 0,
    _sillyJuiceVfxGains: 0,
    _sillyJuicePeakStack: 0,
    percState: null,
    itemState: null,
    pendingLevelUp: false,
    levelUpSelectedIndex: -1,
    levelUpHoverIndex: -1,
    pendingWeaponCard: null,
    _levelUpSessionOfferedWeapons: new Set<string>(),
    _recentUpgradeWeaponIds: [],
    _pendingWeaponCardRarities: new Map<string, UpgradeRarity>(),
    _pendingWeaponCardStats: new Map<string, WeaponStatKey[]>(),
    _pendingWeaponCardLuckMults: new Map<string, number>(),
    _pendingWeaponCardJitters: new Map<string, number>(),
    cachedLevelUpDisplay: null,
    pendingShrineChoice: false,
    shrineSelectedIndex: 0,
    shrineHoverIndex: -1,
    shrineAppliedBuffs: [],
    pendingShrineBuffOptions: null,
    shrineIsGolden: false,
    _lastShrineLuck: 0,
    _shrineDifficultyAccumPct: 0,
    _recentlyOfferedShrineBuffs: [],
    _chestSpin: null,
    _chestLuck: 0,
    pendingChestItems: [],
    pendingChestDimensions: [],
    pendingChestJitters: [],
    pendingChestTiers: [],
    chestHoverIndex: -1,
    chestIsGold: false,
    chestIsElite: false,
    _chestSessionOfferedIds: new Set<string>(),
  };
}

const _itemsStores: Partial<Record<PlayerId, ItemsRunState>> = {};
let _activeItemsPlayerId: PlayerId = 'p1';
// S = the ACTIVE player's store. Every read/write in this module goes
// through it. Single-player: always p1's store.
let S: ItemsRunState = (_itemsStores.p1 = freshItemsRunState());

/** CO-OP PHASE 2+: point the items/percs/choice systems at a player before
 *  simulating their slice of the frame. Creates the store lazily on first
 *  switch. Single-player never calls this. */
export function setActiveItemsAdapterPlayer(id: PlayerId): void {
  _activeItemsPlayerId = id;
  S = _itemsStores[id] ?? (_itemsStores[id] = freshItemsRunState());
}

// BETA 16.98 — Per Dylan: "when i clikc reroll in level up... it just
// rerolles the 1st box. the midle one and far right one stay the same.
// it should reroll all 3 offers, and the offers it give are not allowed
// to be ones that were rolled during this roll session."
//
// Two real bugs in the previous reroll: (a) S.pendingWeaponCard always
// forced slot 2 to be a fixed weapon offer that stuck across rerolls,
// and (b) the random weapon-card injection at line ~172 picked from
// `upgradeable` with no exclusion against previously-offered weapons
// — with limited upgradeable weapons the same one often came back.
//
// Fix: track every perc-name + weapon-id offered THIS level-up
// session (across all rerolls). On reroll, exclude IDs in this set
// from both perc generation (already done via offeredThisLevelUp)
// AND from weapon-card injection. S.pendingWeaponCard is also pushed
// into the excluded set so it doesn't lock slot 2 on reroll.
// (the offered-weapons set lives in the per-player store; the unused
// `pendingWeaponUpgradeCards` array that sat here was dead — deleted 23.66.)

// BETA 19.71 — Cross-level-up dedup: tracks the LAST 1-2 weapons that received
// a stat upgrade card. Prevents "revolver upgrade lvl 4 → revolver upgrade
// lvl 5" back-to-back repetition when the player has only 2-3 equipped weapons.
// PSBOT cycle-30az flagged "build-biased weapon offers" — root cause was the
// 60% stat-upgrade-card rate landing on the same 2-3 equipped weapons over
// and over. Falls back to unfiltered if dedup empties the pool.
const RECENT_UPGRADE_WEAPON_MAX = 2;
function _noteRecentUpgradeWeapon(id: string): void {
  S._recentUpgradeWeaponIds.push(id);
  if (S._recentUpgradeWeaponIds.length > RECENT_UPGRADE_WEAPON_MAX) {
    S._recentUpgradeWeaponIds.shift();
  }
}

// BETA 22.05 — CROSS-RUN variety (Dylan: "items and weapons it gives me aren't cycling —
// each run feels the same"). All the existing anti-repeat memory resets at run start, so it
// never stops repeats ACROSS runs. This module-level ring is NEVER reset — it remembers the
// last few NEW-weapon offers from PRIOR runs so a fresh run surfaces different guns first.
const _CROSS_RUN_WEAPON_MAX = 6;
const _crossRunOfferedWeapons: string[] = [];
function _noteCrossRunWeapon(id: string): void {
  if (!id) return;
  _crossRunOfferedWeapons.push(id);
  while (_crossRunOfferedWeapons.length > _CROSS_RUN_WEAPON_MAX) _crossRunOfferedWeapons.shift();
}

// BETA 22.05 — build the "NEW WEAPON" level-up card for an unowned weapon (factored out of the
// every-3-levels path so the 85% weapon-card injection can also surface fresh guns).
function _makeNewWeaponCard(weapon: { id: string; name: string; iconUrl?: string }, slotIdx: number): LevelUpOptionDisplay {
  return {
    optionIndex: slotIdx,
    type: 'new_weapon',
    weaponId: weapon.id,
    percName: weapon.name,
    percIcon: _moddedCardIcon(weapon.id, weapon.iconUrl ?? ''),
    // BETA 22.87 r4 (Dylan): the card must say what the weapon DOES — the
    // name is already on the top plate. Pull the weapon's own description.
    percDescription: getWeaponDef(weapon.id)?.description || `Equip a new weapon: ${weapon.name}`,
    rarity: 'rare',
    bonusDisplay: 'NEW WEAPON',
    resultLevel: 1,
    accentColor: '#ffd700',
    glowColor: 'rgba(255,215,0,0.5)',
    flavorText: 'A new weapon has entered the chat.',
    statLabel: 'Weapon',
    thisPickDisplay: '',
    newTotalDisplay: '',
  };
}

// (the four per-card offer-time stash maps live in the per-player store —
// see ItemsRunState for the BETA 19.2/19.4/19.14/21.02 stash→apply contract.)

// BETA 19.2 — Megabonk card rarity multipliers (1.0/1.2/1.4/1.6/2.0)
// for displaying the scaled stat deltas on weapon cards.
const CARD_RARITY_MULT_DISPLAY: Record<UpgradeRarity, number> = {
  common: 1.0, uncommon: 1.2, rare: 1.4, epic: 1.6, legendary: 2.0,
};


// ─── Shrine Choice State (per-player, in the store) ─────────────────
// (the unused legacy `shrineOptions` var that sat here was dead — deleted 23.66.)

// BETA 18.18 — Shrine gating context. Engine pushes currentFloor + run
// minutes each tick so generateShrineOptions can gate negative shrines off
// floor 1 / first 2 minutes. Without this, a `+8% Difficulty` shrine could
// pop on the very first wave — player can't tell it's a trap before
// committing, gets swarmed, blames the game. Now negative shrines only
// appear once the player has had time to build.
let _shrineFloor = 1;
let _shrineRunMinutes = 0;
export function setShrineGatingContext(floor: number, runMinutes: number): void {
  _shrineFloor = Math.max(1, floor | 0);
  _shrineRunMinutes = Math.max(0, runMinutes);
}

// ─── Public: access internal state ──────────────────────────────────
export function getPercState(): PeanutPercRunState | null { return S.percState; }
export function getItemState(): BullshiiiiSystemState | null { return S.itemState; }

/** Dev console: instantly give the player any item by ID */
export function debugGiveItem(itemId: string): void {
  if (!S.itemState) { console.warn('[Debug] itemState not ready'); return; }
  addBullshiiii(S.itemState, itemId);
}
export function isPendingLevelUp(): boolean { return S.pendingLevelUp; }
export function clearPendingLevelUp(): void {
  S.pendingLevelUp = false;
  S.cachedLevelUpDisplay = null;
  // BETA 16.98 — reset session-offered weapon set so the next level-up
  // starts with a clean exclusion list. (Perc exclusion is reset by
  // peanutPercSystem.selectLevelUpOption / equivalent end-of-level-up
  // path via state.offeredThisLevelUp.clear().)
  S._levelUpSessionOfferedWeapons.clear();
  // BETA 19.2 — Also clear the stashed weapon card rarities so the next
  // level-up offer rolls fresh rarities.
  S._pendingWeaponCardRarities.clear(); S._pendingWeaponCardStats.clear(); S._pendingWeaponCardLuckMults.clear(); S._pendingWeaponCardJitters.clear();
}
export function getLevelUpSelectedIndex(): number { return S.levelUpSelectedIndex; }
export function setLevelUpSelectedIndex(i: number): void { S.levelUpSelectedIndex = i; }
export function getLevelUpHoverIndex(): number { return S.levelUpHoverIndex; }
export function setLevelUpHoverIndex(i: number): void {
  if (i !== S.levelUpHoverIndex) {
    S.levelUpHoverIndex = i;
    if (i >= 0) playCardHover();
  }
}

// ─── Public: shrine choice state ────────────────────────────────────
export function isPendingShrineChoice(): boolean { return S.pendingShrineChoice; }
export function getShrineSelectedIndex(): number { return S.shrineSelectedIndex; }
export function setShrineSelectedIndex(i: number): void { S.shrineSelectedIndex = i; }

export function getShrineHoverIndex(): number { return S.shrineHoverIndex; }
export function setShrineHoverIndex(i: number): void {
  if (i !== S.shrineHoverIndex) {
    S.shrineHoverIndex = i;
    // BETA 15.69 — Per Dylan: "remove those little pop up sounds, and let
    // the full shrine activation sound complete all the way." Card hover
    // beep suppressed for shrine buff selection so shrine_activate.wav can
    // play uninterrupted. Level-up cards (setLevelUpHoverIndex) untouched —
    // they still chirp on hover.
  }
}

// ─── Public: get display data for HUD ───────────────────────────────
export function getPercSlotDisplayData(): PercSlotDisplay[] {
  if (!S.percState) return [];
  return getPercSlotDisplays(S.percState).filter(s => !s.isEmpty);
}

export function getAllPercSlotDisplayData(): PercSlotDisplay[] {
  if (!S.percState) return [];
  return getPercSlotDisplays(S.percState); // all 4 slots including empty
}

export function getLevelUpOptionDisplayData(): LevelUpOptionDisplay[] {
  // Return cached data if available — prevents flickering from rollRng() on every frame
  if (S.cachedLevelUpDisplay) return S.cachedLevelUpDisplay;

  if (!S.percState) return [];
  const percOptions = getLevelUpOptionDisplays(S.percState);
  if (percOptions.length === 0) return [];

  const result: LevelUpOptionDisplay[] = [...percOptions];

  // Mix in weapon upgrade card(s): randomly 1 or 2 weapon cards per level-up
  // This random roll only happens ONCE (result is cached below)
  const ws = getWeaponSystemState();
  const upgradeable = ws?.weapons.filter(w => {
    const def = getWeaponDef(w.definitionId);
    return def && w.level < def.maxLevel;
  }) ?? [];

  // BETA 15.12 — weapon-upgrade injection chance 25% → 60% per Dylan: "i
  // leveld up 10 times. no weapon buffs. all perks. this is severely
  // unbalanced." Old 25% rate produced 0 weapon offers in 10 level-ups
  // ~5.6% of the time — well within bad-luck range. New 60% rate gives
  // ~6 weapon offers in 10 level-ups (mean), and only 0.01% chance of
  // 0 over 10 levels.
  // BETA 21.49 — Dylan: "weapon level-ups feel like very little." Only ONE weapon
  // card ever injects per level-up, and at 60% ~40% of level-ups showed ZERO.
  // Bumped 0.60 → 0.85 so a weapon upgrade appears in ~85% of level-ups.
  // BETA 21.51 — Dylan: "at least 1 weapon upgrade shown once I have all 4 weapons
  // + all 4 perks." Full loadout = no new percs to chase, so GUARANTEE a weapon card
  // (which weapon stays random). Otherwise the 85% roll above.
  const _weaponsFull = (ws?.weapons.length ?? 0) >= 4;
  const _percsFull = !!S.percState && S.percState.slots.every(s => s.perc !== null);
  const _forceWeaponCard = _weaponsFull && _percsFull;
  const _wantWeaponCard = _forceWeaponCard || rollRng() < 0.85;
  // BETA 22.05 — VARIETY: when you have an OPEN weapon slot, ~55% of weapon cards offer a NEW
  // (unowned) weapon instead of upgrading your starter — so the level-up weapon card isn't your
  // signature gun every single time (Dylan: "the weapons it gives me aren't cycling, each run
  // feels the same"). Cross-run + per-session anti-repeat rotates WHICH new guns appear, so a
  // fresh run surfaces different ones. Once all 4 slots are full it falls back to upgrades.
  let _offeredNewWeapon = false;
  if (_wantWeaponCard && !_forceWeaponCard && (ws?.weapons.length ?? 0) < 4 && rollRng() < 0.55) {
    const _eqIds = new Set((ws?.weapons ?? []).map(w => w.definitionId));
    const _newActive = (_botModeUnlockAll ? WEAPONS : getActiveWeapons())
      .filter(w => !!getWeaponDef(w.id) && !_eqIds.has(w.id) && !S._levelUpSessionOfferedWeapons.has(w.id));
    let _newPool = _newActive.filter(w => !_crossRunOfferedWeapons.includes(w.id));
    if (_newPool.length === 0) _newPool = _newActive;   // ran out of fresh ones → allow recent
    if (_newPool.length > 0) {
      const _np = _newPool[Math.floor(rollRng() * _newPool.length)];
      const _slot = Math.floor(rollRng() * result.length);
      result[_slot] = _makeNewWeaponCard(_np, _slot);
      S._levelUpSessionOfferedWeapons.add(_np.id);
      _noteCrossRunWeapon(_np.id);
      _offeredNewWeapon = true;
    }
  }
  if (!_offeredNewWeapon && upgradeable.length > 0 && _wantWeaponCard) {
    // BETA 16.98 — exclude weapons already offered this level-up session
    // (across all rerolls). If filter empties the pool, fall back to
    // unfiltered — better one repeat than no weapon offer.
    let pool = upgradeable.filter(w => !S._levelUpSessionOfferedWeapons.has(w.definitionId));
    if (pool.length === 0) pool = upgradeable;
    // BETA 19.71 — Cross-level-up dedup. Also exclude weapons that were
    // upgraded in the last 1-2 level-ups so back-to-back same-weapon
    // stat cards don't happen. Falls back to original pool if dedup
    // empties it (i.e., player only has 1-2 equipped weapons total).
    if (S._recentUpgradeWeaponIds.length > 0) {
      const filtered = pool.filter(w => !S._recentUpgradeWeaponIds.includes(w.definitionId));
      if (filtered.length > 0) pool = filtered;
    }
    // Shuffle pool so every weapon has an equal chance
    const shuffledUpgradeable = [...pool];
    for (let s = shuffledUpgradeable.length - 1; s > 0; s--) {
      const r = Math.floor(rollRng() * (s + 1));
      [shuffledUpgradeable[s], shuffledUpgradeable[r]] = [shuffledUpgradeable[r], shuffledUpgradeable[s]];
    }
    const pick = shuffledUpgradeable[0];
    if (pick) {
      // Place in a random slot
      const slot = Math.floor(rollRng() * result.length);
      // BETA 19.2 — Pass player luck so weapon card rarity rolls scale
      // the same way perc cards do.
      const _luck = (S.itemState?.playerStats?.luck ?? 0);
      const card = buildWeaponUpgradeCard(pick.definitionId, slot, _luck);
      if (card) {
        result[slot] = card;
        S._levelUpSessionOfferedWeapons.add(pick.definitionId);
        // BETA 19.71 — Record which weapon got the upgrade slot. Reads in
        // the NEXT level-up to filter this weapon out of the upgrade pool.
        _noteRecentUpgradeWeapon(pick.definitionId);
      }
    }
  }

  // Override last slot with pending weapon offer (new weapon unlock) if any.
  // BETA 16.98 — if S.pendingWeaponCard's weapon is already in the offered
  // set (player saw it last reroll and didn't pick), DON'T re-lock slot 2
  // with it. Player should see fresh content every reroll.
  if (S.pendingWeaponCard && result.length > 0) {
    const _pendingWeaponId = S.pendingWeaponCard.weaponId ?? '';
    if (!_pendingWeaponId || !S._levelUpSessionOfferedWeapons.has(_pendingWeaponId)) {
      result[result.length - 1] = { ...S.pendingWeaponCard, optionIndex: result.length - 1 };
      if (_pendingWeaponId) S._levelUpSessionOfferedWeapons.add(_pendingWeaponId);
    }
  }

  // BETA 15.6 — final-pass dedup safety net per Dylan: "It's always going to
  // be three different ones... There's never going to be anything I intend
  // to there." Use percName + weaponId as the dedup key (LevelUpOptionDisplay
  // doesn't carry percId; same percName == same perc). The earlier perc dedup
  // uses usedPercIds inside generateLevelUpOptions; this is the belt to
  // those suspenders, in case the weapon-card injection or S.pendingWeaponCard
  // override collides with anything else.
  const _seenKeys = new Set<string>();
  const _dedupedResult: LevelUpOptionDisplay[] = [];
  for (const card of result) {
    const key = card.weaponId ? `w:${card.weaponId}` : `p:${card.percName}`;
    if (_seenKeys.has(key)) continue;
    _seenKeys.add(key);
    _dedupedResult.push(card);
  }
  // If dedup dropped any cards, fill the missing slots from fresh percs.
  if (_dedupedResult.length < result.length && S.percState) {
    const freshPercs = getLevelUpOptionDisplays(S.percState).filter(p => !_seenKeys.has(`p:${p.percName}`));
    while (_dedupedResult.length < result.length && freshPercs.length > 0) {
      const filler = freshPercs.shift()!;
      _seenKeys.add(`p:${filler.percName}`);
      _dedupedResult.push({ ...filler, optionIndex: _dedupedResult.length });
    }
  }

  // Cache so every frame returns identical data until choice is made
  S.cachedLevelUpDisplay = _dedupedResult;
  return _dedupedResult;
}

export function getItemInventoryDisplayData(): InventoryDisplay[] {
  if (!S.itemState) return [];
  return getInventoryDisplay(S.itemState);
}

export function getXpBarData(): { level: number; currentXp: number; xpToNextLevel: number; progress: number } {
  if (!S.percState) return { level: 1, currentXp: 0, xpToNextLevel: 100, progress: 0 };
  return getXpBarDisplay(S.percState);
}

// BETA 19.2 — Rarity accent + glow color map for weapon upgrade cards.
// Same palette as item cards (RARITY_COLORS in bunchaBullshiiii.ts) so
// the player gets a consistent visual hierarchy across all systems.
const _CARD_ACCENT_BY_RARITY: Record<UpgradeRarity, { accent: string; glow: string }> = {
  common:    { accent: '#cccccc', glow: 'rgba(204,204,204,0.45)' },
  uncommon:  { accent: '#4a8c2a', glow: 'rgba(74,140,42,0.55)' },
  rare:      { accent: '#6b3fa0', glow: 'rgba(107,63,160,0.55)' },
  epic:      { accent: '#a03f3f', glow: 'rgba(160,63,63,0.55)' },
  legendary: { accent: '#ffd166', glow: 'rgba(255,209,102,0.70)' },
};

// BETA 19.4 — Pretty label for each stat key shown on weapon cards.
// BETA 19.12 — Reload renamed to "Attack Speed" since players think in
// terms of how fast they shoot, not seconds between bursts. The actual
// underlying field is still reloadTime; format function converts the
// delta to a "+X% Attack Speed" boost.
const _STAT_LABELS: Record<WeaponStatKey, string> = {
  damage: 'Damage',
  critChance: 'Crit Chance',
  critDamage: 'Crit Damage',
  knockback: 'Knockback',
  reload: 'Attack Speed',
  projectileCount: 'Projectiles',
  spread: 'Spread',
  projectileSpeed: 'Projectile Speed',
  projectileSize: 'Projectile Size',
  projectilePiercing: 'Piercing',
  projectileBounces: 'Bounces',
};

// BETA 19.4 — Format a single stat's before/after for the card.
// BETA 19.12 — Player-first format. Lead with the boost amount + the
// specific stat name. Megabonk-style. Per Dylan: "they need to know if
// they are getting attack speed boost damage boost, cit chance boost,
// crit damage boost. so on so forth. not 'stronger'. and they need to
// know the percentage". Examples:
//   "+5 Damage"
//   "+10% Damage"        (for fractional damage gains we show %)
//   "+4% Attack Speed"   (converted from reload time delta)
//   "+2% Crit Chance"
//   "+10% Crit Damage"
//   "+1 Projectile"      (integer count, never %)
//   "+1 Pierce"          (integer)
//   "+1 Bounce"          (integer)
// Negative-good stats (spread tightness): "+2.5° Spread Reduction"
// BETA 19.14 — Per-stat MINIMUM MEANINGFUL FLOORS. Per Dylan: "+0.1 is
// not going to cut it for a buff." If a delta would be below its floor,
// _formatStatLine returns null and the 19.11 swap loop tries the next
// stat in the weapon's pool. End result: weapons that have a stat with
// too-low scaling for the current luck/rarity simply don't OFFER that
// stat as a boost — the card always shows something visibly meaningful.
//
// Floors are calibrated so that:
//   • At luck 0 + common rarity (1.0× × 1.0× = baseline), low-scaling
//     stats get filtered out for weapons that barely scale them.
//   • At luck 200 + legendary rarity (2.0× × 2.0× = 4.0× boost), even
//     low-per-level stats clear the floor and become offerable.
//   • Sub-floor displays like "+0.1 Knockback" or "+0.2% Crit Damage"
//     never reach the player.
const _STAT_DISPLAY_FLOORS = {
  // Integers — natural floor is +1, already enforced by integer math.
  damage: 1,
  knockback: 0.3,            // smallest knockback that registers as a real shove
  critChance: 1,             // % — 1% is the smallest crit chance worth showing
  critDamage: 5,             // % — 5% extra crit damage feels like an upgrade
  reload: 2,                 // % attack-speed gain
  projectileSpeed: 3,        // % gain
  projectileSize: 3,         // % gain
  spread: 0.3,               // degrees of tightening
  // Integer-only stats — floor is implicit (+1 minimum from Math.floor):
  projectileCount: 1,
  projectilePiercing: 1,
  projectileBounces: 1,
} satisfies Record<WeaponStatKey, number>;

function _formatStatLine(stat: WeaponStatKey, cur: ReturnType<typeof computeWeaponStats>, nxt: ReturnType<typeof computeWeaponStats>): string | null {
  const label = _STAT_LABELS[stat];
  // Format a number with 1 decimal if it has a meaningful fraction, else integer.
  const _smart = (n: number): string => {
    if (Math.abs(n - Math.round(n)) < 0.05) return String(Math.round(n));
    return n.toFixed(1);
  };
  switch (stat) {
    case 'damage': {
      const d = nxt.damage - cur.damage;
      if (d < _STAT_DISPLAY_FLOORS.damage) return null;
      const pct = cur.damage > 0 ? (d / cur.damage) * 100 : 0;
      // Show both the absolute and the % so the player sees what matters:
      // a +5 damage swing on a 50-damage gun is meaningful (+10%).
      return `+${_smart(d)} ${label} (+${_smart(pct)}%)`;
    }
    case 'critChance': {
      const d = (nxt.critChance - cur.critChance) * 100;
      if (d < _STAT_DISPLAY_FLOORS.critChance) return null;
      return `+${_smart(d)}% ${label}`;
    }
    case 'critDamage': {
      const d = (nxt.critDamage - cur.critDamage) * 100;
      if (d < _STAT_DISPLAY_FLOORS.critDamage) return null;
      return `+${_smart(d)}% ${label}`;
    }
    case 'knockback': {
      const d = nxt.knockback - cur.knockback;
      if (d < _STAT_DISPLAY_FLOORS.knockback) return null;
      return `+${_smart(d)} ${label}`;
    }
    case 'reload': {
      // Player thinks in attack speed, not reload seconds.
      // Attack speed = 1/reloadTime; % gain = (new_atkspd - old_atkspd) / old_atkspd
      //   = (1/nxt - 1/cur) / (1/cur)
      //   = (cur/nxt) - 1
      if (nxt.reloadTime >= cur.reloadTime || nxt.reloadTime <= 0) return null;
      const pctGain = ((cur.reloadTime / nxt.reloadTime) - 1) * 100;
      if (pctGain < _STAT_DISPLAY_FLOORS.reload) return null;
      return `+${_smart(pctGain)}% ${label}`;
    }
    case 'projectileCount': {
      const d = nxt.projectileCount - cur.projectileCount;
      if (d < _STAT_DISPLAY_FLOORS.projectileCount) return null;
      return `+${d} ${d === 1 ? 'Projectile' : 'Projectiles'}`;
    }
    case 'spread': {
      const d = cur.spreadAngle - nxt.spreadAngle;
      if (d < _STAT_DISPLAY_FLOORS.spread) return null;
      return `-${_smart(d)}° Spread`;
    }
    case 'projectileSpeed': {
      const d = nxt.projectileSpeed - cur.projectileSpeed;
      if (d <= 0) return null;
      const pct = cur.projectileSpeed > 0 ? (d / cur.projectileSpeed) * 100 : 0;
      if (pct < _STAT_DISPLAY_FLOORS.projectileSpeed) return null;
      return `+${_smart(pct)}% ${label}`;
    }
    case 'projectileSize': {
      const d = nxt.projectileSize - cur.projectileSize;
      if (d <= 0) return null;
      const pct = cur.projectileSize > 0 ? (d / cur.projectileSize) * 100 : 0;
      if (pct < _STAT_DISPLAY_FLOORS.projectileSize) return null;
      return `+${_smart(pct)}% ${label}`;
    }
    case 'projectilePiercing': {
      const d = nxt.projectilePiercing - cur.projectilePiercing;
      if (d < _STAT_DISPLAY_FLOORS.projectilePiercing) return null;
      return `+${d} Pierce`;
    }
    case 'projectileBounces': {
      const d = nxt.projectileBounces - cur.projectileBounces;
      if (d < _STAT_DISPLAY_FLOORS.projectileBounces) return null;
      return `+${d} ${d === 1 ? 'Bounce' : 'Bounces'}`;
    }
  }
  return null;
}

// ─── Helper: build a weapon upgrade card for the level-up screen ────────────
// BETA 19.2 — Megabonk-style rarity rolling (luck-weighted).
// BETA 19.4 — Megabonk-style STAT POOL selection. Each card:
//   • Rolls card rarity via rollUpgradeRarity(playerLuck)
//   • Picks 1 stat (common) or 2 stats (uncommon+) from the weapon's
//     levelUpStatPool (or auto-derived pool from non-zero *PerLevel fields)
//   • Stashes BOTH rarity + stats in _pendingWeapon* maps so the apply
//     path commits the SAME card the player saw
//   • Displays ONLY the selected stats on the card
//
// Skipping a card (reroll) clears the stash so reroll re-rolls fresh
// rarity + stat selection — a real strategic decision.
function buildWeaponUpgradeCard(weaponId: string, slotIndex: number, playerLuck: number = 0): LevelUpOptionDisplay | null {
  const ws = getWeaponSystemState();
  if (!ws) return null;
  const instance = ws.weapons.find(w => w.definitionId === weaponId);
  if (!instance) return null;
  const def = getWeaponDef(weaponId);
  if (!def || instance.level >= def.maxLevel) return null;

  // BETA 19.2 — Roll card rarity using the same luck-weighted rolling as
  // perc cards. Stash for handleLevelUpSelection to read.
  const cardRarity: UpgradeRarity = rollCardRarity(playerLuck); // BETA 22.87 — 4-tier card roll
  S._pendingWeaponCardRarities.set(weaponId, cardRarity);

  // BETA 19.14 — Lock in the luck-magnitude mult AT OFFER TIME so the
  // number the player sees on the card is the number they get. Stashed
  // for apply path; threaded into _projectStats so the hypothetical nxt
  // reflects it.
  const cardLuckMult = luckMagnitudeMult(playerLuck);
  S._pendingWeaponCardLuckMults.set(weaponId, cardLuckMult);

  // BETA 21.02 — buff variety #3: roll ONE jitter for this card at offer time
  // (rarity-windowed, luck-biased; seeded rollRng). Stashed + threaded into
  // _projectStats so the card preview shows the jittered numbers, and committed
  // at apply (shown==granted). Continuous stats fluctuate to 1 decimal via the
  // existing _smart formatter; integer stats stay whole (Math.floor downstream).
  const cardJitterMult = rollBuffJitter(cardRarity, playerLuck);
  S._pendingWeaponCardJitters.set(weaponId, cardJitterMult);

  // BETA 19.4 — Pick stat(s) from the weapon's pool.
  //   • Common = 1 stat
  //   • Uncommon / Rare / Epic / Legendary = 2 stats (if pool has 2+)
  // BETA 19.11 — If a rolled stat would produce nothing visible (stat
  // already capped, weapon has 0 perLevel for it, etc.), SWAP it for
  // another stat from the pool that DOES produce a real change. Players
  // should never see "Stronger" filler text — every card must show an
  // explicit, precise delta.
  // BETA 19.14 — _formatStatLine now ALSO returns null when the delta is
  // below the per-stat minimum-meaningful floor ("+0.1 Knockback" is
  // unacceptable), so the swap loop also skips visually-pointless boosts.
  const pool = getWeaponStatPool(def);
  const numStats = cardRarity === 'common' ? 1 : Math.min(2, pool.length);
  const shuffled = [...pool].sort(() => rollRng() - 0.5);

  // Helper to compute hypothetical nxt stats for a candidate stat subset.
  // BETA 19.39 — OFF-BY-ONE FIX (paired with the levelUpWeapon fix in
  // weapons.ts). The hypothetical card represents "the card picked to
  // leave the current level". Per Convention B (cardRollsHistory[N] =
  // card-leaving-level-N) we write at index `instance.level`, not
  // `instance.level + 1`. The READ-AT call uses level `instance.level + 1`
  // unchanged — computeWeaponStats's `i=1..lvl=level-1` loop reads
  // indices 1..(instance.level), which includes our new card. Was
  // writing at instance.level+1 → the loop NEVER read it → every stat
  // delta computed as 0 → Dylan saw "Lv 1 → Lv 2 MAXED" on every
  // first-card pick.
  const _cur = computeWeaponStats(def, instance.level, instance.rarity, instance.cardRarityHistory, instance.cardRollsHistory);
  const _hypoRarity = (instance.cardRarityHistory ?? ['common']).slice();
  _hypoRarity[instance.level] = cardRarity;
  const _baseHistory = (instance.cardRollsHistory ?? [{ rarity: 'common', stats: [] as WeaponStatKey[] }]).slice();
  const _projectStats = (candidate: WeaponStatKey[]) => {
    const h = _baseHistory.slice();
    // BETA 19.14 — Project includes luckMult so the displayed nxt reflects
    // BOTH the rarity multiplier AND the luck-scaled magnitude.
    // BETA 19.39 — Index changed from instance.level+1 → instance.level.
    h[instance.level] = { rarity: cardRarity, stats: candidate, luckMult: cardLuckMult, jitterMult: cardJitterMult };
    return computeWeaponStats(def, instance.level + 1, instance.rarity, _hypoRarity, h);
  };

  // Try each shuffled stat. Keep the ones that produce a non-null line.
  // Stop once we have `numStats` good ones.
  const acceptedStats: WeaponStatKey[] = [];
  const statLines: string[] = [];
  for (const stat of shuffled) {
    if (acceptedStats.length >= numStats) break;
    // Project as if THIS stat was rolled alone for the new level.
    // (Multi-stat cards still apply each stat additively at apply time;
    // for DISPLAY we project per-stat so a stat that can't move on its
    // own gets skipped cleanly.)
    const nxtForStat = _projectStats([stat]);
    const line = _formatStatLine(stat, _cur, nxtForStat);
    if (line) {
      acceptedStats.push(stat);
      statLines.push(line);
    }
  }

  // Stash accepted stats so the apply path commits exactly what was shown.
  S._pendingWeaponCardStats.set(weaponId, acceptedStats);

  // Recompute the final projected stats with all accepted stats together
  // for the rest of the card display logic that needs nxt.
  const cur = _cur;
  const nxt = acceptedStats.length > 0 ? _projectStats(acceptedStats) : _cur;
  // Silence the legacy unused var warning — variables defined for downstream
  // code clarity, intentionally unused if statLines populated.
  void nxt; void cur;

  if (statLines.length === 0) statLines.push('MAXED');

  const { accent, glow } = _CARD_ACCENT_BY_RARITY[cardRarity];
  const rarityMult = CARD_RARITY_MULT_DISPLAY[cardRarity];
  const rarityTag = cardRarity.toUpperCase();

  return {
    optionIndex: slotIndex,
    type: 'weapon_upgrade',
    weaponId,
    percName: def.name,
    percIcon: _moddedCardIcon(weaponId, def.spriteUrl || ''),
    percDescription: statLines.join(' · '),
    rarity: cardRarity,
    bonusDisplay: rarityMult >= 1.5
      ? `${rarityTag} WEAPON UPGRADE (${rarityMult}×)`
      : `${rarityTag} WEAPON UPGRADE`,
    resultLevel: instance.level + 1,
    accentColor: accent,
    glowColor: glow,
    flavorText: `Lv ${instance.level} → ${instance.level + 1}`,
    statLabel: 'Weapon',
    thisPickDisplay: '',
    newTotalDisplay: '',
    weaponStatLines: statLines,
    luckBoosted: cardLuckMult > 1.001, // BETA 21.04 — 🍀 when luck raised the stats
  };
}

// ─── Public: handle level-up selection from UI ──────────────────────
export function handleLevelUpSelection(optionIndex: number): boolean {
  if (!S.percState) return false;

  // Check if a weapon offer is in this slot
  const displayOptions = getLevelUpOptionDisplayData();
  const selectedDisplay = displayOptions[optionIndex];
  if (selectedDisplay?.type === ('weapon_upgrade' as string) && selectedDisplay.weaponId) {
    // BETA 19.2 — Pass the rolled card rarity (stashed at offer time).
    // BETA 19.4 — Also pass the rolled stat subset so the per-level
    // history records EXACTLY the card the player saw + clicked.
    const _stashedRarity = S._pendingWeaponCardRarities.get(selectedDisplay.weaponId);
    const _stashedStats = S._pendingWeaponCardStats.get(selectedDisplay.weaponId);
    // BETA 19.14 — Commit the luck-magnitude mult captured at offer time
    // so the apply-path delta MATCHES what the card showed.
    const _stashedLuckMult = S._pendingWeaponCardLuckMults.get(selectedDisplay.weaponId);
    // BETA 21.02 — commit the SAME offer-time jitter the card preview showed.
    const _stashedJitter = S._pendingWeaponCardJitters.get(selectedDisplay.weaponId);
    const _cardRarity = (_stashedRarity ?? (selectedDisplay.rarity as UpgradeRarity)) ?? 'common';
    levelUpWeaponById(selectedDisplay.weaponId, _cardRarity, _stashedStats, _stashedLuckMult, _stashedJitter);
    S._pendingWeaponCardRarities.delete(selectedDisplay.weaponId);
    S._pendingWeaponCardStats.delete(selectedDisplay.weaponId);
    S._pendingWeaponCardLuckMults.delete(selectedDisplay.weaponId);
    S._pendingWeaponCardJitters.delete(selectedDisplay.weaponId);
    // BETA 15.34 — pick audit. Captures weapon ID + level + the offer set
    // (so we know which 2 cards were skipped). Player stats not available
    // at this call site — inferred from neighboring audit entries by t.
    try {
      const ws = getWeaponSystemState();
      const inst = ws?.weapons.find(w => w.definitionId === selectedDisplay.weaponId);
      recordPick({
        t: getCurrentRunTime(),
        source: 'levelup_weapon',
        pickedId: selectedDisplay.weaponId,
        pickedName: selectedDisplay.weaponId,
        pickedValue: inst?.level ?? 1,
        offered: displayOptions.map(d => ({
          id: d.weaponId ?? d.percName ?? '?',
          name: d.weaponId ?? d.percName,
          rarity: d.rarity,
        })),
        before: { level: 0, hp: 0, maxHp: 0, damageBonus: 0, attackSpeedBonus: 0, critChance: 0, critDamage: 0, luck: 0, armor: 0, goldHeld: 0 },
      });
    } catch { /* never crash on audit */ }
    // BETA 16.20 — live-play pick event log
    try {
      recordLivePickEvent({
        t: getCurrentRunTime(),
        source: 'levelup',
        pickedId: selectedDisplay.weaponId,
        offered: displayOptions.map(d => d.weaponId ?? d.percName ?? '?'), // BETA 22.10
        rarity: selectedDisplay.rarity,
      });
    } catch { /* */ }
    S.pendingLevelUp = false; S.cachedLevelUpDisplay = null; S._levelUpSessionOfferedWeapons.clear(); S._pendingWeaponCardRarities.clear(); S._pendingWeaponCardStats.clear(); S._pendingWeaponCardLuckMults.clear(); S._pendingWeaponCardJitters.clear();
    S.levelUpSelectedIndex = -1;
    S.levelUpHoverIndex = -1;
    void endMuffledDuck(); // BETA 16.12 — was resumeBgm
    return true;
  }
  if (selectedDisplay?.type === 'new_weapon' && selectedDisplay.weaponId) {
    addWeaponById(selectedDisplay.weaponId);
    // BETA 15.34 — pick audit for new-weapon equip
    try {
      recordPick({
        t: getCurrentRunTime(),
        source: 'levelup_weapon',
        pickedId: selectedDisplay.weaponId,
        pickedName: selectedDisplay.weaponId,
        pickedValue: 1,  // new weapon = level 1
        offered: displayOptions.map(d => ({
          id: d.weaponId ?? d.percName ?? '?',
          name: d.weaponId ?? d.percName,
          rarity: d.rarity,
        })),
        before: { level: 0, hp: 0, maxHp: 0, damageBonus: 0, attackSpeedBonus: 0, critChance: 0, critDamage: 0, luck: 0, armor: 0, goldHeld: 0 },
      });
    } catch { /* never crash on audit */ }
    // BETA 16.20 — live-play pick event log
    try {
      recordLivePickEvent({
        t: getCurrentRunTime(),
        source: 'levelup',
        pickedId: selectedDisplay.weaponId,
        offered: displayOptions.map(d => d.weaponId ?? d.percName ?? '?'), // BETA 22.10
        rarity: selectedDisplay.rarity,
      });
    } catch { /* */ }
    S.pendingWeaponCard = null;
    S.pendingLevelUp = false; S.cachedLevelUpDisplay = null; S._levelUpSessionOfferedWeapons.clear(); S._pendingWeaponCardRarities.clear(); S._pendingWeaponCardStats.clear(); S._pendingWeaponCardLuckMults.clear(); S._pendingWeaponCardJitters.clear();
    S.levelUpSelectedIndex = -1;
    S.levelUpHoverIndex = -1;
    void endMuffledDuck(); // BETA 16.12 — was resumeBgm
    // BETA 19.9 — defensive focus cleanup so bot path also clears.
    if (getActiveScreen() === 'levelup_cards') {
      unregisterFocusScreen('levelup_cards');
      setActiveScreen(null);
    }
    return true;
  }

  // BETA 16.24 — PSBOT cycle-25 P0.S3: CAPTURE THE OPTION BEFORE
  // selectLevelUpOption runs. Previously the recordLivePickEvent call
  // sat AFTER the select, but selectLevelUpOption clears
  // `state.currentOptions = null` (peanutPercSystem.ts:483) before
  // returning. So the pickEvent silently failed with `realOpt = undefined`
  // — explaining PSBOT's audit finding that "pickEvents missing perc
  // captures." Now we snapshot the option first, then run select, then
  // log. Stat-application diagnosis (T01 / N-perc offer rate / anti-
  // repeat queue verification) is unblocked.
  const _percOptionForLog = S.percState?.currentOptions?.[optionIndex];
  const result = selectLevelUpOption(S.percState, optionIndex);
  if (result.success) {
    try {
      if (_percOptionForLog && _percOptionForLog.perc) {
        recordLivePickEvent({
          t: getCurrentRunTime(),
          source: 'levelup',
          pickedId: _percOptionForLog.perc.id,
          offered: displayOptions.map(d => d.weaponId ?? d.percName ?? '?'), // BETA 22.10
          rarity: _percOptionForLog.rarity,
          stat: _percOptionForLog.perc.stat,
          delta: _percOptionForLog.bonusAmount,
        });
      }
    } catch { /* */ }
    S.pendingLevelUp = false; S.cachedLevelUpDisplay = null; S._levelUpSessionOfferedWeapons.clear(); S._pendingWeaponCardRarities.clear(); S._pendingWeaponCardStats.clear(); S._pendingWeaponCardLuckMults.clear(); S._pendingWeaponCardJitters.clear();
    S.levelUpSelectedIndex = -1;
    S.levelUpHoverIndex = -1;
    S.pendingWeaponCard = null;
    void endMuffledDuck(); // BETA 16.12 — was resumeBgm
    // BETA 19.9 — defensive focus cleanup so bot path also clears.
    if (getActiveScreen() === 'levelup_cards') {
      unregisterFocusScreen('levelup_cards');
      setActiveScreen(null);
    }
    return true;
  }
  return false;
}

// ─── Public: handle chest item acquisition (legacy auto-give, kept for compat) ──────────────────────────
export function handleChestOpen(luck: number = 0, isFreeChest: boolean = false): { itemName: string; itemId: string } | null {
  if (!S.itemState) return null;
  const offer = rollChestItem(S.itemState, luck, isFreeChest);
  if (offer) {
    const result = addBullshiiii(S.itemState, offer.item.id, undefined, luck); // BETA 20.78 — luck-scale item values
    if (result.success) {
      return { itemName: offer.item.name, itemId: offer.item.id };
    }
  }
  return null;
}

// ─── Shrine Buff Pool ────────────────────────────────────────────────────────
interface ShrineBuff {
  id: string;
  label: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'negative';
  stat: string;
  value: number;
  pct: boolean;
  /** BETA 20.97 — true when LUCK raised this buff's number above its base
   *  (set at offer time in generateShrineOptions). Drives the 🍀 card badge. */
  luckBoosted?: boolean;
}

const SHRINE_RARITY_COLORS: Record<string, string> = {
  // BETA 20.28 — snapped to the canonical 3-tier palette so shrine cards match
  // level-up / chest exactly: common green, rare/epic purple, legendary gold.
  common: '#39ff14',
  uncommon: '#39ff14',
  rare: '#b15cff',
  epic: '#b15cff',
  legendary: '#ffd700',
  negative: '#ff3333',
};

// BETA 15.47 — Big magnitude pass per PSBOT cycle-17 audit.
// Damage values −30-40%, lifesteal −50%, atk-speed −35-40%, crit −40%,
// max HP −50%, elite damage −50%, gold gain −50%. NO CAPS per
// BALANCING_PHILOSOPHY.md — per-pick magnitude tuning only.
// Targets: finalDamagePct max 293% → ~150%, finalLifestealPct max 76% →
// ~30-40%, finalAttackSpeedPct max 120% → ~60-70%, finalMaxHP max 567 → ~300-350.
export const SHRINE_BUFF_POOL: ShrineBuff[] = [
  // ── Common tier ──
  { id: 'dmg_12',   label: '+8% Damage',           rarity: 'common',   stat: 'damageBonus',          value: 8,    pct: true  },  // BETA 15.47: 12 → 8
  { id: 'dmg_10',   label: '+6% Damage',           rarity: 'common',   stat: 'damageBonus',          value: 6,    pct: true  },  // BETA 15.47: 10 → 6
  { id: 'shield_5', label: '+5 Shield',             rarity: 'common',   stat: 'maxShield',             value: 5,    pct: false },
  { id: 'pickup',   label: '+20% Pickup Range',     rarity: 'common',   stat: 'pickupRange',           value: 20,   pct: true  },
  { id: 'critdmg',  label: '+6% Crit Damage',       rarity: 'common',   stat: 'critDamage',            value: 6,    pct: true  },  // BETA 15.47: 10 → 6 (crit damage −40% per PSBOT)
  { id: 'luck',     label: '+8 Luck',               rarity: 'common',   stat: 'luck',                  value: 8,    pct: false },  // BETA 20.65 — label/unit truth-sync: luck is applied FLAT (p.luck += 8) and every other luck source says "+N luck", but this label claimed "%". Value unchanged. (BETA 16.27 history: 5 → 8 per cycle-30 pacing audit.)
  { id: 'projcnt',  label: '+1 Projectile Count',   rarity: 'common',   stat: 'projectileCountBonus',  value: 1,    pct: false },
  { id: 'regen',    label: '+4 HP Regen',            rarity: 'common',   stat: 'hpRegen',               value: 4,    pct: false },  // BETA 20.77 — 20 → 4 (god-mode audit: one common pick used to TRIPLE base regen)
  { id: 'kback_10', label: '+10% Knockback',        rarity: 'common',   stat: 'knockbackBonus',        value: 10,   pct: true  },
  { id: 'lsteal_6', label: '+3% Lifesteal',         rarity: 'common',   stat: 'lifesteal',             value: 3,    pct: true  },  // BETA 15.47: 6 → 3 (lifesteal −50%, infinite-HP loop fix)
  // BETA 17.03 — PSBOT cycle-30u A5: common move-speed shrine bumped 8 → 12.
  { id: 'spd_8',    label: '+12% Movement Speed',   rarity: 'common',   stat: 'moveSpeed',             value: 12,   pct: true  },
  { id: 'xp_20',    label: '+20% XP Gain',          rarity: 'common',   stat: 'xpMultiplier',          value: 20,   pct: true  },
  { id: 'gold_15',  label: '+15% Gold Gain',        rarity: 'common',   stat: 'goldMultiplier',        value: 15,   pct: true  },  // BETA 15.54: 7 → 15 (REVERT 15.47 secondary gold nerf per Dylan)
  { id: 'evade_5',  label: '+5% Evasion',           rarity: 'common',   stat: 'evasion',               value: 5,    pct: true  },
  { id: 'armor_5',  label: '+5 Armor',              rarity: 'common',   stat: 'armor',                 value: 5,    pct: false },
  // BETA 21.18 — Dylan: small + simple dash shrine buffs (faster recharge, longer dash).
  { id: 'dash_rchg',label: '+15% Dash Recharge',    rarity: 'common',   stat: 'dashRecharge',          value: 15,   pct: true  },
  { id: 'dash_dist',label: '+20% Dash Distance',    rarity: 'common',   stat: 'dashDuration',          value: 20,   pct: true  },
  // ── Uncommon tier ──
  { id: 'kback_12', label: '+12% Knockback',        rarity: 'uncommon', stat: 'knockbackBonus',        value: 12,   pct: true  },
  { id: 'atk_72',   label: '+6% Attack Speed',     rarity: 'uncommon', stat: 'attackSpeedBonus',      value: 6,    pct: true  },  // BETA 19.97: 4 → 6 (Dylan audit — uncommon was 1.4 perc-picks worth = invisible). Rare atk_84 cascaded to 8 to preserve rarity ladder.
  { id: 'crit_5',   label: '+5% Crit Chance',       rarity: 'uncommon', stat: 'critChance',            value: 5,    pct: true  },  // BETA 19.97: 3 → 5 (Dylan audit — uncommon was ~1 perc-pick of Vision = invisible)
  { id: 'thorns',   label: '+15% Thorns',           rarity: 'uncommon', stat: 'thorns',                value: 15,   pct: true  },
  { id: 'lsteal_10',label: '+5% Lifesteal',         rarity: 'uncommon', stat: 'lifesteal',             value: 5,    pct: true  },  // BETA 15.47: 10 → 5 (−50%)
  // BETA 19.91 — Crit Damage shrine ladder (full balance audit found only
  // common tier existed = no upgrade path for crit builds). Adding the
  // uncommon/rare/epic/legendary tiers to match every other stat family.
  { id: 'critdmg_u', label: '+10% Crit Damage',     rarity: 'uncommon', stat: 'critDamage',            value: 10,   pct: true  },
  // ── Rare tier ──
  { id: 'atk_84',   label: '+8% Attack Speed',     rarity: 'rare',     stat: 'attackSpeedBonus',      value: 8,    pct: true  },  // BETA 19.97: 6 → 8 (cascade — uncommon atk_72 bumped 4→6 in 19.97, rare must stay above uncommon)
  { id: 'proj_bnc', label: '+1 Projectile Bounce',  rarity: 'rare',     stat: 'projectileBounces',     value: 1,    pct: false },
  { id: 'range_u',  label: '+150 Lock-On Range',    rarity: 'uncommon', stat: 'rangeBonus',            value: 150,  pct: false },
  { id: 'overheal', label: '+30 Overheal',          rarity: 'rare',     stat: 'maxShield',             value: 30,   pct: false },
  // BETA 16.91 — PSBOT cycle-30l §3 + Dylan "yes" — shield builds
  // were anemic because shield only protected 8.5% of damage in
  // cycle-30k data. New maxShield-MULT shrines stack on top of flat
  // shield items so a "tank" build with Underwear + Wyfe Beater +
  // Adrenaline Pod can actually scale shield late-run.
  { id: 'shield_mult_20',label: '+20% Max Shield',    rarity: 'uncommon', stat: 'maxShieldMult',        value: 20,   pct: true  },
  { id: 'shield_mult_50',label: '+50% Max Shield',    rarity: 'epic',     stat: 'maxShieldMult',        value: 50,   pct: true  },
  // BETA 16.36 — Dylan: "shield regen should be on items right. or added to shrines?"
  // Three tiers covering the full ladder: common +1/sec, uncommon +2/sec,
  // rare +4/sec. Additive on top of the 1.0/sec base.
  { id: 'shregen_1',label: '+1 Shield Regen/sec',     rarity: 'common',   stat: 'shieldRegen',           value: 1,    pct: false },
  { id: 'shregen_2',label: '+2 Shield Regen/sec',     rarity: 'uncommon', stat: 'shieldRegen',           value: 2,    pct: false },
  { id: 'shregen_4',label: '+4 Shield Regen/sec',     rarity: 'rare',     stat: 'shieldRegen',           value: 4,    pct: false },
  { id: 'elite_dmg',label: '+10% Elite Damage',    rarity: 'rare',     stat: 'eliteDamageBonus',     value: 10,   pct: true  },  // BETA 15.47: 20 → 10 (−50%)
  // BETA 19.91 — Crit Damage shrine ladder, rare tier.
  { id: 'critdmg_r', label: '+15% Crit Damage',    rarity: 'rare',     stat: 'critDamage',            value: 15,   pct: true  },
  // ── Epic tier ──
  { id: 'dmg_epic', label: '+15% Damage',           rarity: 'epic',     stat: 'damageBonus',           value: 15,   pct: true  },  // BETA 15.47: 25 → 15
  { id: 'hp_epic',  label: '+40 Max HP',            rarity: 'epic',     stat: 'maxHp',                 value: 40,   pct: false },  // BETA 19.97: 25 → 40 (Dylan audit — epic shrine needs meaningful HP jump vs perc Vitamins per-pick)
  // BETA 19.91 — Crit Damage shrine ladder, epic tier.
  { id: 'critdmg_e', label: '+25% Crit Damage',    rarity: 'epic',     stat: 'critDamage',            value: 25,   pct: true  },
  // ── Legendary — Golden Charge Shrine only ──
  { id: 'leg_dmg',   label: '+25% Damage',          rarity: 'legendary', stat: 'damageBonus',          value: 25,   pct: true  },  // BETA 15.47: 40 → 25
  { id: 'leg_hp',    label: '+80 Max HP',           rarity: 'legendary', stat: 'maxHp',               value: 80,   pct: false },  // BETA 19.97: 50 → 80 (Dylan audit — LEGENDARY golden shrine deserves a real HP buff, not 33% of base)
  // BETA 17.03 — PSBOT cycle-30u A5: legendary move-speed shrine bumped 16 → 20.
  { id: 'leg_spd',   label: '+20% Movement Speed',  rarity: 'legendary', stat: 'moveSpeed',            value: 20,   pct: true  },
  { id: 'leg_atk',   label: '+10% Attack Speed',    rarity: 'legendary', stat: 'attackSpeedBonus',     value: 10,   pct: true  },  // BETA 19.89: full audit — was +6% legendary = +6% rare (atk_84). Legendary must beat rare. Restored to 10 (the original BETA 15.48 pre-nerf value), since rare 15.67 fix already put atk_84 at 6%.
  { id: 'leg_crit',  label: '+8% Crit Chance',      rarity: 'legendary', stat: 'critChance',           value: 8,    pct: true  },  // BETA 19.97: 4 → 8 (Dylan audit — LEGENDARY was 1.4 perc-picks of Vision, weak. Crit-chance jitter still bounded by cycle-21 evidence; this is one shrine drop, not per-pick scaling.)
  { id: 'leg_ovheal',label: '+60 Overheal',         rarity: 'legendary', stat: 'maxShield',            value: 60,   pct: false },
  { id: 'leg_proj',  label: '+2 Projectile Count',  rarity: 'legendary', stat: 'projectileCountBonus', value: 2,    pct: false },
  { id: 'leg_range', label: '+300 Lock-On Range',   rarity: 'legendary', stat: 'rangeBonus',           value: 300,  pct: false },
  { id: 'leg_gold',  label: '+30% Gold Gain',       rarity: 'legendary', stat: 'goldMultiplier',       value: 30,   pct: true  },  // BETA 15.54: 15 → 30 (REVERT 15.47 secondary gold nerf)
  // BETA 19.91 — Crit Damage shrine ladder, legendary tier (top tier).
  { id: 'leg_critdmg', label: '+50% Crit Damage', rarity: 'legendary', stat: 'critDamage',           value: 50,   pct: true  },
  { id: 'leg_steal', label: '+10% Lifesteal',       rarity: 'legendary', stat: 'lifesteal',            value: 10,   pct: true  },  // BETA 15.47: 20 → 10 (−50%)
  { id: 'leg_regen', label: '+10 HP Regen',         rarity: 'legendary', stat: 'hpRegen',              value: 10,   pct: false },  // BETA 20.77 — 50 → 10 (god-mode audit; ladder preserved)
  { id: 'difficulty',label:'+8% Difficulty',        rarity: 'negative',  stat: 'difficulty',           value: 8,    pct: true  },
];

export function getShrineIsGolden(): boolean { return S.shrineIsGolden; }

// BETA 16.33 — Cumulative `+N% Difficulty` shrine buff total. Reset
// alongside S.shrineAppliedBuffs on run reset. Engine.ts reads this each
// tick and folds it into waveState.difficultyMultiplier (more enemies
// only — never enemy stats). See applyAllBonuses() switch case.
export function getShrineDifficultyPct(): number { return S._shrineDifficultyAccumPct; }

// BETA 16.21 — PSBOT cycle-24 P0.5: anti-repeat queue for shrine offers,
// matching the level-up perc anti-repeat (peanutPercSystem). Tracks the last
// 12 buff IDs that were OFFERED (not necessarily picked) and filters them
// out of subsequent rolls. Soft-fallback if the filtered pool drops too low.
// Reset by resetShrineOfferQueue() on new-run.
export function resetShrineOfferQueue(): void { S._recentlyOfferedShrineBuffs = []; }

/** Return a copy of all shrine/chest buffs applied this run (for balance analysis) */
export function getAppliedBuffsList(): Array<{ stat: string; value: number; pct: boolean; label: string }> {
  return [...S.shrineAppliedBuffs];
}

/** Return per-slot perc contributions for balance analysis */
export function getPercContributions(): Array<{
  percName: string;
  stat: string;
  isPercent: boolean;
  level: number;
  accumulatedBonus: number;
  accentColor: string;
}> {
  if (!S.percState) return [];
  return S.percState.slots
    .filter(s => s.perc !== null && s.level > 0)
    .map(s => ({
      percName: s.perc!.name,
      stat: s.perc!.stat,
      isPercent: s.perc!.isPercent,
      level: s.level,
      accumulatedBonus: s.accumulatedBonus,
      accentColor: s.perc!.accentColor ?? '#ffffff',
    }));
}

export interface ShrineBuffOption {
  index: number;
  label: string;
  rarity: string;
  color: string;
  /** BETA 22.92 — internal stat key (== SHRINE_BUFF_POOL.stat), used to pick
   *  the hand-made shrine tile art (shrineTiles.ts). */
  stat: string;
  /** BETA 22.92 — the value to draw in the tile's value zone, e.g. "+2.7%" or
   *  "+40". Sign + % included; already luck/rarity/stack-scaled. */
  valueText: string;
  /** BETA 20.26 — structured stat delta for the live stats-panel projection.
   *  Omitted for negative/difficulty shrines (no player-stat row to project). */
  previewMods?: { stat: string; value: number; isPercent: boolean }[];
  /** BETA 20.97 — true when LUCK raised this buff above base → draw a 🍀 badge. */
  luckBoosted?: boolean;
}

export function getShrineBuffDisplayOptions(): ShrineBuffOption[] {
  if (!S.pendingShrineBuffOptions) return [];
  return S.pendingShrineBuffOptions.map((buff, i) => {
    const _v = Math.round((buff.value ?? 0) * 10) / 10;
    const _sign = _v < 0 ? '' : '+';   // difficulty value is positive; sign stays +
    return {
      index: i,
      label: buff.label,
      rarity: buff.rarity,
      color: SHRINE_RARITY_COLORS[buff.rarity] || '#39ff14',
      stat: buff.stat,
      valueText: `${_sign}${_v}${buff.pct ? '%' : ''}`,
      previewMods: (buff.stat && buff.rarity !== 'negative')
        ? [{ stat: buff.stat, value: buff.value, isPercent: !!buff.pct }]
        : undefined,
      luckBoosted: !!buff.luckBoosted,
    };
  });
}

// ─── Chest Spin State (3-card slot-machine, staggered left→right landing) ────

// Landing times per slot (ms after spin start)
const SLOT_LAND_TIMES = [1900, 2280, 2660];

interface SlotState {
  item: BullshiiiiItem;   // the item this slot WILL land on (pre-determined)
  pool: BullshiiiiItem[]; // shuffled real item pool; final item appended at end
  index: number;           // current display index into pool
  nextFlipTime: number;    // ms (performance.now()) for next item change
  landed: boolean;
  justLanded: boolean;     // true for exactly one getChestSpinDisplay() call
}

interface ChestSpinState {
  chestType: 'goop' | 'gold' | 'elite';
  slots: SlotState[];      // always 3
  startTime: number;       // ms (performance.now())
  allLandedAt: number | null; // ms when last slot landed
}


// Mega Bonk-style weighted rarity picker: roll rarity first, then pick item of that rarity.
// Uses full item database — not limited to loadout selection or starter pool.
function _rollRarity(weights: Record<ItemRarity, number>): ItemRarity {
  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  let roll = rollRng() * total;
  for (const [r, w] of Object.entries(weights) as [ItemRarity, number][]) {
    roll -= w;
    if (roll <= 0) return r;
  }
  return 'common';
}

// BETA 16.29 — PSBOT cycle-30e: returns picks PLUS parallel rolled
// dimensions array. Dimension is rolled (weighted) only for items the
// player ALREADY OWNS that have buffDimensions. Otherwise undefined at
// that index.
function _pickChestItemsWithDimensions(chestType: 'goop' | 'gold' | 'elite'): {
  picks: BullshiiiiItem[];
  dimensions: (BuffDimension | undefined)[];
} {
  const picks = _pickChestItems(chestType);
  // BETA 17.09 — PSBOT cycle-30aa #12: UC06 silent-no-op investigation
  // confirmed a broader silent-bug pattern. The original code only rolled
  // a dimension for ALREADY-OWNED items, meaning FIRST pickup of any pure-
  // dimensional item (UC06, C06, C08, UC02, UC05, R07, E01, etc. — every
  // BETA 17.01-17.04 conversion with empty statModifiers) landed with
  // rolledDimension=undefined → slot.pickedDimensions={} → apply loop sees
  // `picks[statField] ?? 0 → 0` → ZERO stat applied. Card said "+12 armor"
  // and the player got nothing. Cycle-30aa Killa run had UC06 equipped,
  // finalArmor=0 + finalEvasion=0 — matches this bug exactly. Fix: roll
  // the dimension on first pickup too; addBullshiiii now seeds
  // pickedDimensions[statField]=1 in the new-slot branch.
  const dimensions = picks.map(pick => {
    if (!pick.buffDimensions || pick.buffDimensions.length === 0) return undefined;
    return rollItemDimension(pick) ?? undefined;
  });
  return { picks, dimensions };
}

function _pickChestItems(chestType: 'goop' | 'gold' | 'elite'): BullshiiiiItem[] {
  // BETA 20.04 — Filter by unlock state. Dev users (DevPeanut etc.) have all
  // items in S.itemState.unlockedItemIds via applyDevUserUnlocks; regular users
  // only see items they've unlocked. Previously this function used the full DB
  // for chest pools — every regular user saw every item from chest #1 regardless
  // of unlock conditions. Now: pool is filtered to unlocked-only.
  const allActive = getActiveBullshiiii();
  const unlockedSet = S.itemState?.unlockedItemIds;
  const _unlockedItems = unlockedSet
    ? allActive.filter(i => unlockedSet.has(i.id))
    : allActive;
  // BETA 21.86 — Dylan: "if max stack is ever 1 (or N) on any item, once I have it (N of
  // it) it should be removed from the offer pool — that slot becomes another chance at
  // something I can still use." Filter out items already at their maxStacks cap. (The
  // single-item roll paths already did this; the live 3-card chest path never did — that's
  // why a maxed/owned-unique item kept reappearing.) Fall back to the unlocked pool only
  // if you've maxed EVERYTHING you've unlocked, so the chest never goes blank.
  const _st = S.itemState;
  const _availItems = _st ? _unlockedItems.filter(i => isItemAvailableForPickup(_st, i)) : _unlockedItems;
  const allItems = _availItems.length > 0 ? _availItems : _unlockedItems;
  const byRarity: Partial<Record<ItemRarity, BullshiiiiItem[]>> = {};
  for (const item of allItems) {
    (byRarity[item.rarity] ??= []).push(item);
  }

  // BETA 20.29/20.30 — Dylan: "GOLD CHEST DOES NOT MEAN LEGENDARY... all legendary
  // items need to circulate in paid AND free chest the same" + "all rare, common
  // and legendary items should circulate in all chests." BOTH chest types now roll
  // ONE shared, PURE 3-TIER distribution (common / rare / legendary). The chest
  // TYPE only governs free-vs-paid — NOT loot quality — and every active item,
  // legendaries included, circulates in every chest equally.
  //   uncommon + epic are 0: the game is 3-tier now and ALL uncommon/epic items
  //   are status:'shelved' (getActiveBullshiiii excludes them), so zeroing those
  //   weights strands nothing and stops wasted rolls falling back to common.
  //   Numbers are tunable.
  const _UNIFIED_CHEST_WEIGHTS: Record<ItemRarity, number> = {
    common: 60, uncommon: 0, rare: 33, epic: 0, legendary: 7,
  };
  // BETA 20.56 — Blue Elite Chest: HIGHER-END MIX, not legendary-only. Per
  // Dylan: "the same rolls as a regular chest, just with better buffs — not
  // only legendaries, just higher-end." So it's the normal 3-tier spread
  // skewed UP — mostly rare + legendary, occasional common (vs the regular
  // chest's 60/33/7). Was 100% legendary (BETA 20.49), which flooded.
  const _ELITE_CHEST_WEIGHTS: Record<ItemRarity, number> = {
    common: 10, uncommon: 0, rare: 50, epic: 0, legendary: 40,
  };
  // BETA 21.73 — Dylan: "GOLD FREE CHESTS are prioritizing legendary + rare with
  // high buffs — free should NOT have any bonus for opening them." Free room
  // (gold) chests now roll a PLAIN, common-heavy spread (no rarity boost) AND get
  // NO luck scaling (gated below). Paid (goop) chests keep the 60/33/7 spread +
  // luck. So opening a free chest is baseline loot; the good stuff is on paid.
  const _GOLD_FREE_CHEST_WEIGHTS: Record<ItemRarity, number> = {
    common: 82, uncommon: 0, rare: 16, epic: 0, legendary: 2,
  };
  const weights: Record<'goop' | 'gold' | 'elite', Record<ItemRarity, number>> = {
    goop: _UNIFIED_CHEST_WEIGHTS,
    gold: _GOLD_FREE_CHEST_WEIGHTS,
    elite: _ELITE_CHEST_WEIGHTS,
  };

  // BETA 20.77 — LUCK finally reaches live chest rolls (audit: Dylan's 42
  // chests at 341 luck all rolled the flat 60/33/7 — the luck-weighted
  // roller in bunchaBullshiiii was dead code on this path). Luck multiplies
  // the legendary + rare weights; _rollRarity normalizes. At 341 luck a
  // regular chest ≈ 38% common / 45% rare / 17% legendary (was 60/33/7).
  const _chestLuckW: Record<ItemRarity, number> = { ...weights[chestType] };
  {
    // BETA 21.73 — luck only boosts PAID (goop) + elite chests. Free (gold) room
    // chests stay plain — no luck-driven rarity bump (Dylan: "free should not have
    // any bonus for opening them").
    const _clk = Math.max(0, S._chestLuck);
    if (_clk > 0 && chestType !== 'gold') {
      _chestLuckW.legendary = _chestLuckW.legendary * (1 + _clk / 120);
      _chestLuckW.rare = _chestLuckW.rare * (1 + _clk / 300);
    }
  }

  const seen = new Set<string>();
  const picks: BullshiiiiItem[] = [];
  let attempts = 0;

  while (picks.length < 3 && attempts < 40) {
    attempts++;
    const rarity = _rollRarity(_chestLuckW);
    const pool = byRarity[rarity] ?? byRarity['common'] ?? allItems;
    if (pool.length === 0) continue;
    // BETA 21.64 — weighted by the dashboard "Spawn weight" (default 1 = uniform,
    // so this is identical to the old random pick until a weight is edited).
    let _swTotal = 0;
    for (const _it of pool) _swTotal += spawnWeightOf(_it);
    let _swR = rollRng() * _swTotal;
    let candidate = pool[pool.length - 1];
    for (const _it of pool) { _swR -= spawnWeightOf(_it); if (_swR <= 0) { candidate = _it; break; } }
    // BETA 20.03 — Also check cross-chest dedup ring buffer (_recentChestPicks).
    // Was: in-chest `seen` Set prevented dupes within ONE chest, but
    // _wasRecentlyPicked (rolling 8-item window across last few chests) was
    // only used by the SINGLE-item rollChestItem path, not _pickChestItems.
    // That's why Dylan saw UC03 in chest #1 AND chest #2 back-to-back.
    // Now: skip a candidate if it was offered in the recent ring buffer
    // (provided we have at least 8 retries left to find an alternative).
    if (seen.has(candidate.id)) continue;
    if (_wasRecentlyPicked(candidate.id) && attempts < 32) continue;
    // BETA 22.07 — Dylan: "i reroll and one of the items stays the same — it should be all 3
    // random NEW items." S._chestSessionOfferedIds holds everything shown THIS chest (across
    // rerolls; cleared on close/skip), so excluding it guarantees a reroll never repeats a
    // currently-shown item. Fallback after attempts>=36 so a thin pool can still fill 3.
    if (S._chestSessionOfferedIds.has(candidate.id) && attempts < 36) continue;
    // BETA 22.10 — Dylan: thin the common flood. The always-on commons (Wrench/C03, etc.) are
    // force-added to every run + maxStacks 99, so they used to fill multiple chest slots. Allow
    // at most ONE of them per chest so the cheap items don't crowd out variety. Fallback once
    // attempts run low so a thin pool can still fill 3.
    if (ALWAYS_ON_BULLSHIIII_IDS.has(candidate.id) && attempts < 30 &&
        picks.some(p => ALWAYS_ON_BULLSHIIII_IDS.has(p.id))) continue;
    seen.add(candidate.id);
    picks.push(candidate);
    // Record into the ring buffer so the NEXT chest can avoid this item too.
    _recordChestPick(candidate.id);
  }

  // Pad with unseen items from any rarity if we couldn't fill 3
  if (picks.length < 3) {
    for (const item of allItems) {
      if (picks.length >= 3) break;
      if (!seen.has(item.id)) { seen.add(item.id); picks.push(item); }
    }
  }

  return picks;
}

// Smooth ease-out deceleration curve — more steps = smoother slowdown before landing
function _rateForRemaining(msRemaining: number): number {
  if (msRemaining > 1400) return 95;    // ~10fps — start speed
  if (msRemaining > 1050) return 130;   // ~7.7fps
  if (msRemaining > 750)  return 190;   // ~5fps
  if (msRemaining > 500)  return 280;   // ~3.5fps
  if (msRemaining > 280)  return 420;   // ~2.4fps
  return 640;                           // ~1.6fps — near-stop crawl before the landing snap
}


// BETA 22.89 — WILD CARD TIER roll, shared by the open path (startChestSpin)
// and the reroll path (generateChestOptions). Free 'gold' machines roll twice
// and keep the better pull; elite machines always vend legendary (the old
// guaranteed-legendary chest promise).
function _rollWildCardTiers(count: number, chestType: 'goop' | 'gold' | 'elite'): UpgradeRarity[] {
  const _tierRank: Record<string, number> = { common: 0, uncommon: 0, rare: 1, epic: 2, legendary: 3 };
  return Array.from({ length: count }, () => {
    if (chestType === 'elite') return 'legendary' as UpgradeRarity;
    const a = rollCardRarity(S._chestLuck);
    if (chestType === 'gold') {
      const b = rollCardRarity(S._chestLuck);
      return (_tierRank[b] ?? 0) > (_tierRank[a] ?? 0) ? b : a;
    }
    return a;
  });
}

export function startChestSpin(chestType: 'goop' | 'gold' | 'elite', playerLuck = 0): void {
  S._chestLuck = playerLuck;
  // Roll the 3 items now — they ARE the final choices.
  // BETA 16.29 — PSBOT cycle-30e: also roll dimensions for owned items.
  const { picks, dimensions } = _pickChestItemsWithDimensions(chestType);
  S.pendingChestItems = picks;   // pre-set so chest_choice screen works immediately
  S.pendingChestDimensions = dimensions;
  // BETA 21.00 — one jitter per pick, locked in at offer time (seeded rollRng).
  S.pendingChestJitters = picks.map(p => rollBuffJitter((p.rarity as UpgradeRarity) ?? 'common', S._chestLuck));
  // BETA 22.89 — the machine's OPEN path must roll wild-card tiers too. Only
  // generateChestOptions (the reroll path) rolled them, so every fresh vend
  // displayed common (S.pendingChestTiers[i] ?? 'common') no matter the luck.
  S.pendingChestTiers = _rollWildCardTiers(picks.length, chestType);
  S.chestIsGold = chestType === 'gold';
  S.chestIsElite = chestType === 'elite'; // BETA 20.49 — for reroll legendary-force
  S.chestHoverIndex = -1;

  let allItems = getActiveBullshiiii();
  if (allItems.length < 4) allItems = [...allItems, ...allItems];

  const now = performance.now();
  const slots: SlotState[] = picks.map((item) => {
    const shuffled = [...allItems].sort(() => rollRng() - 0.5);
    // Remove the final item so it only appears when landing
    const pool = shuffled.filter(i => i.id !== item.id);
    pool.push(item); // land on the real item at the very end
    return {
      item,
      pool,
      index: 0,
      nextFlipTime: now + 60,
      landed: false,
      justLanded: false,
    };
  });

  S._chestSpin = { chestType, slots, startTime: now, allLandedAt: null };
}

export interface SlotSpinDisplay {
  iconUrl: string;
  item: BullshiiiiItem;
  landed: boolean;
  justLanded: boolean;
}

export interface ChestSpinDisplay {
  slots: SlotSpinDisplay[];
  color: string;
  allLanded: boolean;
  chestType: 'goop' | 'gold' | 'elite';
}

export function getChestSpinDisplay(): ChestSpinDisplay | null {
  if (!S._chestSpin) return null;
  const now = performance.now();

  let allLanded = true;
  for (let si = 0; si < S._chestSpin.slots.length; si++) {
    const slot = S._chestSpin.slots[si];
    slot.justLanded = false;

    if (slot.landed) continue;

    const landAt = S._chestSpin.startTime + SLOT_LAND_TIMES[si];
    if (now >= landAt) {
      slot.index = slot.pool.length - 1; // last entry is always the final item
      slot.landed = true;
      slot.justLanded = true;
      if (si === S._chestSpin.slots.length - 1) {
        S._chestSpin.allLandedAt = now;
      }
      continue;
    }

    allLanded = false;
    if (now >= slot.nextFlipTime) {
      slot.index = (slot.index + 1) % (slot.pool.length - 1); // cycle through pool, skip last
      const msRemaining = landAt - now;
      slot.nextFlipTime = now + _rateForRemaining(msRemaining);
    }
  }

  const color = S._chestSpin.chestType === 'gold'
    ? '#ffd700'
    : (S._chestSpin.chestType === 'elite' ? '#00e5ff' : '#00bfff');
  return {
    slots: S._chestSpin.slots.map(s => {
      const displayItem = s.landed ? s.item : s.pool[s.index];
      return {
        iconUrl: displayItem.iconUrl,
        item: displayItem,
        landed: s.landed,
        justLanded: s.justLanded,
      };
    }),
    color,
    allLanded: allLanded && S._chestSpin.allLandedAt !== null,
    chestType: S._chestSpin.chestType,
  };
}

export function isChestSpinDone(): boolean {
  if (!S._chestSpin || !S._chestSpin.allLandedAt) return false;
  return (performance.now() - S._chestSpin.allLandedAt) >= 700;
}

export function getChestSpinType(): 'goop' | 'gold' | 'elite' {
  return S._chestSpin?.chestType ?? 'goop';
}

export function clearChestSpin(): void {
  S._chestSpin = null;
}

// ─── Chest Choice State (per-player, in the store) ──────────────────────────
// (see ItemsRunState for the offer-time parallel arrays' stash→apply
// contracts: dimensions 16.29, jitters 21.00, wild-card tiers 22.89,
// session dedup 16.98.)
export function getChestIsGold(): boolean { return S.chestIsGold; }
export function getChestHoverIndex(): number { return S.chestHoverIndex; }
export function setChestHoverIndex(i: number): void {
  if (i !== S.chestHoverIndex) {
    S.chestHoverIndex = i;
    if (i >= 0) playCardHover();
  }
}

export interface ChestItemDisplay {
  index: number;
  itemId: string;
  name: string;
  description: string;
  flavorText: string;
  rarity: ItemRarity;
  iconUrl: string;
  color: string;
  /** BETA 21.04 — true when LUCK raised this item's buff numbers → 🍀 badge. */
  luckBoosted?: boolean;
  /** BETA 16.29 — PSBOT cycle-30e: present when the offer is for an
   *  OWNED item with buffDimensions. Renderer shows
   *  "dimensionLabel: +current → +next" arrow format. */
  dimensionPreview?: {
    label: string;
    currentValue: string;
    nextValue: string;
  };
  /** BETA 17.72 — synergy tag (damage / health / defense / luck / xp / utility).
   *  Bot uses this to bias picks toward the build's primary synergy
   *  (e.g. sniper bot prefers damage+crit; aura bot prefers area+health).
   *  Not used for UI rendering — purely a hint to the auto-playtester. */
  synergy?: string;
  /** BETA 20.26 — structured stat delta(s) the item grants, so the stats panel
   *  can project the hovered card's effect in real time. Mirrors the item's
   *  statModifiers (raw per-pickup values). */
  previewMods?: { stat: string; value: number; isPercent: boolean }[];
  /** BETA 22.89 — WILD CARDS: rolled tier of this pull (art shell + stack mult). */
  tier: UpgradeRarity;
  /** Baked wild-card art (/wild-cards/<tier>/<file>.webp) — null for legacy items. */
  cardArt: string | null;
  /** ONE-word category for the card's bottom plate (Dylan spec). */
  category: string;
  /** Stacks of the base effect this pull grants (×1/2/3/5 by tier). */
  tierStacks: number;
}

export function generateChestOptions(chestType: 'goop' | 'gold' | 'elite' = 'goop'): void {
  S.chestIsGold = chestType === 'gold';
  S.chestIsElite = chestType === 'elite'; // BETA 20.49 — keep reroll legendary-forced
  // BETA 20.29 — Dylan: legendaries circulate in paid AND free chests the same.
  // Reroll now delegates to the SAME unified weighted roll as the initial open
  // (_pickChestItems) instead of the old hard tier filter (gold=rare/epic,
  // goop=common) which excluded legendaries from every reroll. Reroll === open:
  // identical distribution, same dedup, and it also rolls buff dimensions (the
  // old path silently dropped them on reroll). S._chestSessionOfferedIds is still
  // tracked below so a reroll keeps avoiding what it just showed you.
  const { picks, dimensions } = _pickChestItemsWithDimensions(chestType);
  S.pendingChestItems = picks;
  S.pendingChestDimensions = dimensions;
  // BETA 21.00 — reroll gets a FRESH jitter per pick (same as initial open).
  S.pendingChestJitters = picks.map(p => rollBuffJitter((p.rarity as UpgradeRarity) ?? 'common', S._chestLuck));
  // BETA 22.89 — roll each offer's WILD CARD TIER (perk-card weights + luck).
  S.pendingChestTiers = _rollWildCardTiers(picks.length, chestType);
  for (const p of picks) S._chestSessionOfferedIds.add(p.id);
  S.chestHoverIndex = -1;
}

export function getChestOptionDisplayData(): ChestItemDisplay[] {
  return S.pendingChestItems.map((item, i) => {
    const _tier = S.pendingChestTiers[i] ?? 'common';
    const display: ChestItemDisplay = {
      index: i,
      itemId: item.id,
      name: item.name,
      description: item.description,
      flavorText: item.flavorText,
      rarity: item.rarity,
      iconUrl: item.iconUrl,
      color: ITEM_RARITY_COLORS_BS[item.rarity]?.text || '#ccc',
      // BETA 22.89 — Wild Card presentation
      tier: _tier,
      cardArt: wildCardArtPath(item.id, _tier),
      category: WILD_CARD_BY_ITEM[item.id]?.category ?? 'UTILITY',
      tierStacks: WILD_CARD_TIER_STACKS[_tier] ?? 1,
      synergy: (item as { synergy?: string }).synergy,
      // BETA 21.04 — 🍀 when luck raised this item's (non-luck) buff numbers.
      luckBoosted: luckValueMult(S._chestLuck) > 1.001
        && !!(item.statModifiers && item.statModifiers.some(m => m.stat !== 'luck')),
      // BETA 20.26 — feed the live stats-panel projection from the item's
      // structured stat modifiers (never parsed from description text).
      // BETA 20.78 — truth-sync: show the LUCK-SCALED value you'll actually
      // get (matches the luckMultSum scaling applied at pickup). The luck
      // stat itself is shown unscaled (it isn't scaled at apply — no feedback).
      // BETA 21.00 — buff variety: fold in the per-pick jitter (S.pendingChestJitters[i],
      // rolled once at offer time) so the shown value FLUCTUATES + reads in decimals,
      // and matches exactly what addBullshiiii grants at pickup. Luck stat stays unscaled.
      previewMods: (item.statModifiers && item.statModifiers.length > 0)
        ? item.statModifiers.map(m => ({
            stat: m.stat,
            value: m.stat === 'luck'
              ? m.value
              : Math.round(m.value * luckValueMult(S._chestLuck) * (S.pendingChestJitters[i] ?? 1) * 10) / 10,
            isPercent: m.isPercent,
          }))
        : undefined,
    };
    // BETA 16.29 — PSBOT cycle-30e: if a dimension was rolled at
    // offer time, build the current → next preview from the slot's
    // existing pickedDimensions count (or 0 if first dim pick on
    // this owned item).
    const dim = S.pendingChestDimensions[i];
    if (dim) {
      const slot = S.itemState?.inventory.find(s => s.item.id === item.id);
      const currentPicks = slot?.pickedDimensions?.[dim.statField] ?? 0;
      const fmtVal = (v: number): string => {
        switch (dim.unit) {
          case 'pct':  return `${v.toFixed(0)}%`;
          case 'sec':  return `${v.toFixed(1)}s`;
          case 'wu':   return `${v.toFixed(0)}wu`;
          case 'mult': return `${v.toFixed(2)}×`;
          default:     return `${v.toFixed(0)}`;
        }
      };
      display.dimensionPreview = {
        label: dim.label.replace(/%(d|\.0?f|\.1?f)%?/g, '').trim() || dim.statField,
        currentValue: fmtVal(currentPicks * dim.bonusPerStack),
        nextValue: fmtVal((currentPicks + 1) * dim.bonusPerStack),
      };
    }
    return display;
  });
}

export function handleChestItemSelection(index: number): { itemName: string; rolledDimensionLabel?: string } | null {
  if (!S.itemState || index < 0 || index >= S.pendingChestItems.length) return null;
  // BETA 16.98 — clear the session-offered set when this chest closes.
  S._chestSessionOfferedIds.clear();
  const item = S.pendingChestItems[index];
  // BETA 16.29 — PSBOT cycle-30e: pass the rolled dimension (if any) so
  // addBullshiiii can increment slot.pickedDimensions for the picked
  // dimension. First-pickup paths receive undefined and just initialize
  // an empty pickedDimensions dict.
  const rolledDim = S.pendingChestDimensions[index];
  // BETA 17.12 — PSBOT cycle-30ae carry-forward: Dylan complained dimensional
  // items "auto every time" with no feedback on which dimension rolled.
  // Return the rolled label so engine can spawn a pickupNotification telling
  // player exactly what they got. (Full clickable choice UI deferred to 17.13.)
  let rolledDimensionLabel: string | undefined;
  if (rolledDim && item.buffDimensions) {
    const bonus = rolledDim.bonusPerStack;
    const unit = rolledDim.unit;
    const sign = bonus >= 0 ? '+' : '';
    const valueStr = unit === 'pct' ? `${sign}${bonus}%` : `${sign}${bonus}`;
    rolledDimensionLabel = `${valueStr} ${rolledDim.label.replace(/^[+\-]?\d+%?\s*/, '').trim()}`;
  }
  // BETA 20.78 — S._chestLuck = the player's TOTAL luck when this chest was
  // opened (set in startChestSpin). Passed so the item's buff VALUES snapshot-
  // scale with luck (luckMultSum in bunchaBullshiiiiSystem).
  // BETA 21.00 — pass the SAME offer-time jitter the card preview showed, so the
  // granted value == the shown value (no re-roll).
  // BETA 22.89 — WILD CARDS: the rolled TIER grants stacks of the base effect
  // (×1/2/3/5 = Dylan's perk-card multiplier pattern; common == the exact old
  // approved item values). Extra stacks apply without the rolled dimension so
  // the dimension is committed exactly once.
  const _stacks = WILD_CARD_TIER_STACKS[S.pendingChestTiers[index] ?? 'common'] ?? 1;
  addBullshiiii(S.itemState, item.id, rolledDim, S._chestLuck, S.pendingChestJitters[index] ?? 1);
  for (let _s = 1; _s < _stacks; _s++) {
    addBullshiiii(S.itemState, item.id, undefined, S._chestLuck, S.pendingChestJitters[index] ?? 1);
  }
  // BETA 16.20 — PSBOT cycle-23 P1.5: live-play pick event log. Source
  // pulled from the engine-side _lastChestPaid flag (set at chest open).
  try {
    const _isPaid = !!(window as unknown as { __gameState?: { _lastChestPaid?: boolean } }).__gameState?._lastChestPaid;
    recordLivePickEvent({
      t: getCurrentRunTime(),
      source: _isPaid ? 'chest_paid' : 'chest_free',
      pickedId: item.id,
      offered: S.pendingChestItems.map(i => i.id), // BETA 22.10 — full offered set (still set here; cleared below)
      rarity: item.rarity,
    });
  } catch { /* */ }
  S.pendingChestItems = []; S.pendingChestDimensions = []; S.pendingChestJitters = []; S.pendingChestTiers = [];
  S.chestHoverIndex = -1;
  void endMuffledDuck(); // BETA 16.12 — was resumeBgm
  // BETA 19.9 — defensive focus cleanup at the pick site itself.
  if (getActiveScreen() === 'chest_cards') {
    unregisterFocusScreen('chest_cards');
    setActiveScreen(null);
  }
  return { itemName: item.name, rolledDimensionLabel };
}

// ─── Public: handle shrine perc acquisition (now generates 3 buff choices) ───

/**
 * Generate 3 shrine buff options for the player to choose from.
 * Returns true if options were generated (game should pause to show choices).
 */
export function generateShrineOptions(isGolden = false, playerLuck = 0): boolean {
  S.shrineIsGolden = isGolden;
  // BETA 20.77 — remember luck for the reroll path (itemsAdapter re-calls
  // this with S.shrineIsGolden only).
  S._lastShrineLuck = playerLuck;
  let options: ShrineBuff[];

  // BETA 16.21 — PSBOT cycle-24 P0.5: shared helper to pick 3 distinct buffs
  // from a candidate pool with anti-repeat queue + stat-category soft-dedup.
  // Mirrors peanutPercSystem.generateLevelUpOptions logic so shrine variety
  // matches level-up variety. recentlyOffered queue is updated AFTER picking.
  const _pickThreeWithDedup = (pool: ShrineBuff[]): ShrineBuff[] => {
    const recentSet = new Set(S._recentlyOfferedShrineBuffs);
    // Try filtering against the recently-offered queue first.
    let candidates = pool.filter(b => !recentSet.has(b.id));
    // Soft-fallback: if anti-repeat filter drops the pool below 3 distinct
    // buff IDs, fall back to the unfiltered pool. No soft-locks.
    const distinctFilteredIds = new Set(candidates.map(b => b.id));
    if (distinctFilteredIds.size < 3) candidates = pool;

    // Stat-category soft-dedup: don't offer two buffs with the same `stat`
    // in the same roll if the candidate pool has 3+ distinct stat categories.
    const distinctStats = new Set(candidates.map(b => b.stat));
    const enforceCatDedup = distinctStats.size >= 3;

    const shuffled = [...candidates].sort(() => rollRng() - 0.5);
    const seenIds = new Set<string>();
    const usedStats = new Set<string>();
    const picked: ShrineBuff[] = [];
    for (const buff of shuffled) {
      if (seenIds.has(buff.id)) continue;
      if (enforceCatDedup && usedStats.has(buff.stat)) continue;
      seenIds.add(buff.id);
      usedStats.add(buff.stat);
      picked.push(buff);
      if (picked.length >= 3) break;
    }
    // If stat-category dedup left us short of 3, top up ignoring stat dedup.
    if (picked.length < 3) {
      for (const buff of shuffled) {
        if (seenIds.has(buff.id)) continue;
        seenIds.add(buff.id);
        picked.push(buff);
        if (picked.length >= 3) break;
      }
    }
    return picked;
  };

  if (isGolden) {
    // Golden shrine: only legendary buffs (no traps)
    const legendaryPool = SHRINE_BUFF_POOL.filter(b => b.rarity === 'legendary');
    options = _pickThreeWithDedup(legendaryPool);
  } else {
    // Normal shrine: weighted pool (common=5x, uncommon=3x, rare=2x, epic=1x)
    // BETA 14.73 cycle-9: defensive buffs get a 1.4× offer-rate multiplier. Cyc-9
    // analysis: bots had no defensive cushion (evasion 5%, armor 0, lifesteal 0-10,
    // maxHP stuck at base) → died in 30s flat trickle damage. Bump defensive
    // shrine pool exposure so bots actually accumulate survival stats.
    const _DEFENSIVE_STATS = new Set([
      'evasion', 'armor', 'maxHp', 'maxShield', 'maxShieldMult', 'thorns',
      'hpRegen', 'lifesteal', 'shield', 'shieldRegen',
    ]);
    const weighted: ShrineBuff[] = [];
    for (const buff of SHRINE_BUFF_POOL) {
      if (buff.rarity === 'negative' || buff.rarity === 'legendary') continue;
      let copies = buff.rarity === 'common' ? 5 : buff.rarity === 'uncommon' ? 3 : buff.rarity === 'rare' ? 2 : 1;
      // BETA 20.77 — LUCK finally reaches shrines (Dylan at 341 luck: "all
      // shrines gave the basic buffs"). Higher tiers surface more often as
      // luck grows: rare +1 copy per 150 luck, epic +1 per 100.
      if (buff.rarity === 'rare') copies += Math.floor(Math.max(0, playerLuck) / 150);
      else if (buff.rarity === 'epic') copies += Math.floor(Math.max(0, playerLuck) / 100);
      if (_DEFENSIVE_STATS.has(buff.stat)) copies = Math.round(copies * 1.4); // BETA 14.73 cycle-9 §04
      for (let i = 0; i < copies; i++) weighted.push(buff);
    }
    options = _pickThreeWithDedup(weighted);
    // 15% chance to replace last option with a negative/trap (untouched by 16.21)
    // BETA 18.18 — Gate: NEGATIVE shrines (+8% Difficulty, etc) only spawn
    // once the player has had time to build. Showing a "+8% MORE ENEMIES"
    // shrine on Floor 1 / minute 1 is a trap: player doesn't know what
    // "difficulty" means yet and accepts it accidentally → swarmed → dies.
    // Now: floor 2+ OR after 2 minutes on floor 1.
    const _negativeGateOpen = _shrineFloor >= 2 || _shrineRunMinutes >= 2;
    if (_negativeGateOpen && rollRng() < 0.15 && options.length === 3) {
      const neg = SHRINE_BUFF_POOL.find(b => b.rarity === 'negative');
      if (neg) options[2] = neg;
    }
  }

  // BETA 16.21 — PSBOT cycle-24 P0.5: prepend offered buff IDs onto the
  // anti-repeat queue and cap. BETA 16.24 — bumped 12 → 18 (= 6 shrines
  // of memory) to match the level-up queue bump for variety.
  const offeredIds = options.map(o => o.id);
  S._recentlyOfferedShrineBuffs = [...offeredIds, ...S._recentlyOfferedShrineBuffs].slice(0, 18);

  // BETA 20.97 — BUFF VARIETY + LUCK. Every shrine buff now FLUCTUATES per pick
  // (jitter) AND reads in decimals for % stats, instead of the same fixed integer
  // every time (Dylan: "the buff numbers... are not fixed either? that feels
  // clunky. that is why we have luck"). Two factors, both the SAME math the
  // approved level-up percs use:
  //   • rollBuffJitter(rarity, luck) — per-pick fluctuation (≈±15%), high luck
  //     biased toward the upper end. Gives variety even at 0 luck.
  //   • luckValueMult(luck) — luck scales the magnitude (×1.0 at 0 → ×2.0 at 400+).
  // Rolled ONCE here at offer time, frozen into the option (value + label rewritten
  // together) so the card shows EXACTLY what it grants — getShrineBuffDisplayOptions
  // + handleShrineSelection both read this same array, no re-roll. Seeded rollRng.
  // Skips negative buffs (no lucky traps) and luck-stat buffs (no self-feedback).
  // % stats round to 1 decimal; flat point stats (armor/HP/shield) stay whole.
  {
    const _shrineLuckMult = luckValueMult(playerLuck);
    const _luckRaised = _shrineLuckMult > 1.001;
    options = options.map(b => {
      if (b.rarity === 'negative' || b.stat === 'luck') return b;
      const _jitter = rollBuffJitter(b.rarity, playerLuck); // 'negative' already excluded above
      // BETA 21.34 — MEGABONK-STYLE "START SMALL, SCALE PER STACK" (Dylan: shrines
      // "start way too powerful... should be smaller, then scale up"). The FIRST time
      // you take a given STAT the buff is half-strength; each additional stack of the
      // SAME stat grows it +25% (0.5× → 0.75× → 1.0× → 1.25× ...). Early picks are
      // modest; committing to one stat over a run is what makes it strong. Scales the
      // value AND the card label together so the card shows EXACTLY what it grants.
      const _stacks = S.shrineAppliedBuffs.filter(s => s.stat === b.stat).length;
      const _stackMult = 0.5 + 0.25 * _stacks;
      const _raw = b.value * _jitter * _shrineLuckMult * _stackMult;
      const _scaled = b.pct ? Math.round(_raw * 10) / 10 : Math.round(_raw);
      const _safe = _scaled > 0 ? _scaled : b.value; // never zero out a buff
      return {
        ...b,
        value: _safe,
        label: b.label.replace(/[\d.]+/, String(_safe)),
        luckBoosted: _luckRaised,
      };
    });
  }

  S.pendingShrineBuffOptions = options;
  S.pendingShrineChoice = true;
  S.shrineSelectedIndex = 0;
  S.shrineHoverIndex = -1;
  return options.length > 0;
}

/**
 * Get shrine option display data — kept for backward compatibility.
 * New callers should use getShrineBuffDisplayOptions().
 */
export function getShrineOptionDisplayData(): LevelUpOptionDisplay[] {
  return [];
}

/**
 * BETA 14.16 — Programmatically apply a shrine buff by id (used by WeaponLab
 * randomizer to skip the choice-screen UI). Pushes onto S.shrineAppliedBuffs
 * which applyAllBonusesToPlayer reads each frame to compose stats. Returns
 * true on success, false if no shrine with that id was found.
 */
export function applyShrineBuffById(shrineId: string): boolean {
  const buff = SHRINE_BUFF_POOL.find(b => b.id === shrineId);
  if (!buff) return false;
  S.shrineAppliedBuffs.push({ stat: buff.stat, value: buff.value, pct: buff.pct, label: buff.label });
  // BETA 14.57 — fire shrine-select tracker for BOTH paths (handleShrineSelection
  // real-game choice + applyBuild lab randomizer). Was only firing from handleShrineSelection
  // which meant 0 shrine events in lab CSVs even when user "did a shrine" via
  // RANDOMIZE. Now every shrine application generates an event regardless of path.
  _shrineSelectTracker?.(buff.label, buff.stat, buff.value, buff.pct);
  return true;
}

/** BETA 14.16 — Clear all shrine buffs. Used by WeaponLab applyBuild() so
 *  each new build starts with a fresh shrine slate. */
export function clearAppliedShrineBuffs(): void {
  S.shrineAppliedBuffs = [];
}

// BETA 14.56 — Shrine-select tracker registration. itemsAdapter can't import
// from weaponLab (would be a circular dep — weaponLab imports from
// itemsAdapter). engine.ts wires this at init by registering a callback
// that pushes a 'shrine' event into weaponLab's _localEventLog.
type ShrineSelectTracker = (label: string, stat: string, value: number, pct: boolean) => void;
let _shrineSelectTracker: ShrineSelectTracker | null = null;
export function registerShrineSelectTracker(t: ShrineSelectTracker | null): void {
  _shrineSelectTracker = t;
}

/**
 * Handle the player's shrine buff selection.
 * Returns the label of the chosen buff, or null if failed.
 */
export function handleShrineSelection(optionIndex: number): { percName: string } | null {
  if (!S.pendingShrineBuffOptions || optionIndex < 0 || optionIndex >= S.pendingShrineBuffOptions.length) return null;
  const buff = S.pendingShrineBuffOptions[optionIndex];
  const _shrineOffered = S.pendingShrineBuffOptions.map(o => o.id); // BETA 22.10 — capture before clear
  S.shrineAppliedBuffs.push({ stat: buff.stat, value: buff.value, pct: buff.pct, label: buff.label });
  S.pendingShrineChoice = false;
  S.pendingShrineBuffOptions = null;
  S.shrineSelectedIndex = 0;
  S.shrineHoverIndex = -1;
  // BETA 14.56 — fire tracker callback (engine.ts pushes shrine event +
  // calls trackShrineApply for the live shrineTracker col).
  _shrineSelectTracker?.(buff.label, buff.stat, buff.value, buff.pct);
  // BETA 16.20 — PSBOT cycle-23 P1.5: live-play pick event log
  try {
    recordLivePickEvent({
      t: getCurrentRunTime(),
      source: 'shrine',
      pickedId: buff.id,
      offered: _shrineOffered, // BETA 22.10
      rarity: buff.rarity,
      stat: buff.stat,
      delta: buff.value,
    });
  } catch { /* */ }
  // BETA 15.69 — playShrineActivate() moved to shrines.ts at the channel-
  // complete moment (see comment there). Removed from here so it doesn't
  // double-fire on click after already playing at activation.
  // BETA 15.88 — Resume BGM (paused at shrine activate complete).
  void endMuffledDuck(); // BETA 16.12 — was resumeBgm
  // BETA 19.9 — defensive focus cleanup so bot path also clears.
  if (getActiveScreen() === 'shrine_cards') {
    unregisterFocusScreen('shrine_cards');
    setActiveScreen(null);
  }
  return { percName: buff.label };
}

// ─── Reroll helpers ──────────────────────────────────────────────────────────

/**
 * Reroll level-up options — regenerates entirely new cards, guaranteed different
 * from the current set (up to 5 attempts to find a distinct combination).
 */
export function rerollLevelUpOptions(): void {
  if (!S.percState) { S.cachedLevelUpDisplay = null; return; }

  // Snapshot the current card IDs so we can avoid the same combination
  const prevIds = S.cachedLevelUpDisplay
    ? S.cachedLevelUpDisplay.map(o => `${o.type}:${o.percName}:${o.weaponId ?? ''}`).join('|')
    : '';

  for (let attempt = 0; attempt < 5; attempt++) {
    // BETA 20.77 — audit fix: rerolled cards rolled rarity at luck 0
    // (hardcoded). Pass real luck like the normal level-up path does.
    generateLevelUpOptions(S.percState, S.itemState?.playerStats?.luck ?? 0);
    S.cachedLevelUpDisplay = null; // force re-render from new currentOptions
    // BETA 19.2 — clear stashed weapon card rarities so the reroll path
    // produces FRESH rarity rolls (a reroll is a real strategic choice;
    // your Legendary card could become Common, or vice versa).
    // BETA 19.28 — Also clear S._pendingWeaponCardLuckMults (added in 19.14
    // but missed here + at the perc-pick path) so reroll re-locks the
    // luck-magnitude mult for each fresh weapon card.
    S._pendingWeaponCardRarities.clear(); S._pendingWeaponCardStats.clear(); S._pendingWeaponCardLuckMults.clear(); S._pendingWeaponCardJitters.clear();
    const newOptions = getLevelUpOptionDisplayData();
    const newIds = newOptions.map(o => `${o.type}:${o.percName}:${o.weaponId ?? ''}`).join('|');
    if (newIds !== prevIds || attempt === 4) break; // got something different
  }
}

/** Regenerate shrine options in-place (same golden flag). */
export function rerollShrineOptions(): void {
  generateShrineOptions(S.shrineIsGolden, S._lastShrineLuck); // BETA 20.77 — keep luck on reroll
}

/** Regenerate chest options in-place (same chest type). */
export function rerollChestOptions(): void {
  generateChestOptions(S.chestIsElite ? 'elite' : (S.chestIsGold ? 'gold' : 'goop'));
}

/** Skip (dismiss) the active shrine choice without applying anything. */
export function skipShrineChoice(): void {
  S.pendingShrineChoice = false;
  S.pendingShrineBuffOptions = null;
  S.shrineSelectedIndex = 0;
  S.shrineHoverIndex = -1;
  // BETA 15.88 — Resume BGM (paused at shrine activate complete).
  void endMuffledDuck(); // BETA 16.12 — was resumeBgm
}

/** Skip (dismiss) the active chest choice without picking anything. */
export function skipChestChoice(): void {
  S.pendingChestItems = []; S.pendingChestDimensions = []; S.pendingChestJitters = []; S.pendingChestTiers = [];
  S.chestHoverIndex = -1;
  // BETA 16.98 — clear session-offered set on skip too.
  S._chestSessionOfferedIds.clear();
  void endMuffledDuck(); // BETA 16.12 — was resumeBgm
}

/** Legacy auto-assign (fallback if no options available) */
export function handleShrineActivation(): { percName: string } | null {
  if (!S.percState) return null;
  const slots = S.percState.slots;
  const emptySlot = slots.findIndex(s => s.perc === null);

  if (emptySlot >= 0) {
    const equippedIds = new Set(slots.filter(s => s.perc).map(s => s.perc!.id));
    const available = PEANUT_PERCS.filter(p => !equippedIds.has(p.id));
    if (available.length > 0) {
      const perc = available[Math.floor(rollRng() * available.length)];
      slots[emptySlot].perc = perc;
      slots[emptySlot].level = 1;
      slots[emptySlot].accumulatedBonus = perc.bonusPerLevel;
      return { percName: perc.name };
    }
  }

  const upgradeable = slots.filter(s => s.perc && s.level < s.perc.maxLevel);
  if (upgradeable.length > 0) {
    const slot = upgradeable[Math.floor(rollRng() * upgradeable.length)];
    if (slot.perc) {
      slot.level++;
      slot.accumulatedBonus += slot.perc.bonusPerLevel;
      return { percName: slot.perc.name };
    }
  }

  return null;
}

// ─── BETA 13.103 — Helper used by advanced perc eval ─────────────────
/** Apply a perc stat bonus to a Player object, handling the common shape
 *  conversions (fraction → integer % for percent stats, flat for the rest).
 *  Negative values are valid (used by trade-off percs like Sponge Me Daddy). */
function _applyPercStatBonus(
  p: Player,
  stat: PercStat,
  amount: number,        // raw bonus value: e.g. 15 means +15 (or +15% if isPercent)
  isPercent: boolean,
): void {
  if (amount === 0) return;
  switch (stat) {
    case 'damage':           p.damageBonus += amount; break;            // already in integer-%
    case 'attackSpeed':      p.attackSpeedBonus += amount; break;
    case 'maxHp':
      // maxHp percent bumps apply via maxHp variable in caller; we approximate
      // by scaling p.maxHp directly. Flat values add the raw number.
      if (isPercent) p.maxHp = Math.max(1, Math.round(p.maxHp * (1 + amount / 100)));
      else p.maxHp += amount;
      break;
    case 'moveSpeed':
      if (isPercent) p.speed = Math.max(20, p.speed * (1 + amount / 100));
      else p.speed += amount;
      break;
    case 'dodgeChance':      p.evasion += amount; break;
    case 'critChance':       p.critChance += amount; break;
    case 'goldBonus':
      if (isPercent) p.goldMultiplier *= Math.max(0, 1 + amount / 100);
      else p.goldMultiplier += amount;
      break;
    case 'knockback':        p.knockbackBonus += amount; break;
    case 'projectileSpeed':  p.projectileSpeedBonus += amount; break;
    case 'hpRegen':          p.hpRegen += amount; break;
    case 'shield':           p.armor += amount; break;   // legacy mapping (kept for any existing data)
    case 'xpBonus':
      // BETA 20.66 — XP% sources now ADD like damage% does everywhere else.
      // Five source families were COMPOUNDING (×): three +25% XP items gave
      // +95% instead of +75%, silently feeding the level-up spam.
      if (isPercent) p.xpMultiplier += amount / 100;
      else p.xpMultiplier += amount;
      break;
    case 'attackSize':       p.attackAreaBonus += amount; break;
    case 'luck':             p.luck += amount; break;
    // BETA 15.13 — new stat domains opened by the new-perk redesign (PSBOT RUN spec).
    // Each new perk anchors on a UNIQUE stat — zero overlap with the original 14 T-perks.
    case 'lifesteal':        p.lifesteal += amount; break;            // N01 Sponge Me Daddy
    case 'armor':            p.armor += amount; break;                // N02 Hutch's Armor (now a real stat — was 'shield' mapping)
    case 'thorns':           p.thorns += amount; break;               // N03 Stepmom's Pills
    case 'critDamage':       p.critDamage += amount; break;           // N04 Not Blue Chew
    case 'projectileCount':
      // INTEGER-ROLLOVER stat. Per-pick is fractional (0.2 default) — accumulated
      // sums to a fractional value, but the player sees only Math.floor() of the
      // total. So 5 picks of +0.2 = 1.0 = +1 projectile. Math.floor on the raw
      // amount keeps the sum predictable when called frame-by-frame.
      p.projectileCountBonus += amount;
      break;
    case 'overheal': {
      // Flat overheal pool. Engine reads p.maxHp + overheal as the cap when picking
      // up health orbs, and the overheal portion DOESN'T regen but persists until
      // damage chips it away. BETA 15.13 — uses player.overheal field if present,
      // else falls back to expanding maxHp.
      const pAny = p as unknown as Record<string, number>;
      if (typeof pAny.overheal === 'number') {
        pAny.overheal += amount;
      } else {
        p.maxHp += amount;  // fallback if overheal field doesn't exist yet
      }
      break;
    }
    case 'projectilePierce': {
      // INTEGER-ROLLOVER stat (see projectileCount note).
      // BETA 15.13 — added p.projectilePierceBonus on the player. Engine should
      // floor and add to per-weapon piercing at fire time.
      const pAny = p as unknown as Record<string, number>;
      if (typeof pAny.projectilePierceBonus === 'number') {
        pAny.projectilePierceBonus += amount;
      } else {
        // Field not yet on Player — write it lazily. Engine read should null-coalesce.
        pAny.projectilePierceBonus = amount;
      }
      break;
    }
    case 'pickupRange':
      // BETA 15.13 — additive to player.pickupRange. Note: existing player.pickupRange
      // is in world-units (px). isPercent multiplies; flat adds.
      if (isPercent) p.pickupRange = Math.max(0, p.pickupRange * (1 + amount / 100));
      else p.pickupRange += amount;
      break;
    case 'eliteDamage':
      // Applied vs elite AND boss variants (boss included since BETA 20.89).
      // weaponAdapter.ts onEnemyHit reads eliteDamageBonus at the damage site.
      p.eliteDamageBonus += amount;
      break;
    case 'projectileBounce':
      // INTEGER-ROLLOVER stat.
      p.projectileBounceBonus += amount;
      break;
    case 'phaseChance': {
      // BETA 15.13 — separate from dodgeChance (T05 Reflexes). Roll on contact damage:
      // if phase succeeds, no damage taken AND no iframe granted. Engine should
      // check this BEFORE evasion/iframe in the player damage path.
      const pAny = p as unknown as Record<string, number>;
      if (typeof pAny.phaseChanceBonus === 'number') {
        pAny.phaseChanceBonus += amount;
      } else {
        pAny.phaseChanceBonus = amount;
      }
      break;
    }
  }
}

// ─── Apply all bonuses to player stats in real-time ─────────────────
function applyAllBonusesToPlayer(state: GameState): void {
  const p = state.player;

  // Reset computed bonus stats each frame
  p.luck = 0;
  p.critChance = 5;
  p.critDamage = 0;
  p.lifesteal = 0;
  p.armor = 0;
  p.evasion = 0;
  p.knockbackBonus = 0;
  p.projectileCountBonus = 0;
  p.rangeBonus = 0;
  p.dashRechargeBonus = 0;   // BETA 21.18 — reset each frame before shrine buffs re-apply
  p.dashDurationBonus = 0;
  // BETA 21.39 — Dylan: "each level up you collect from a further distance naturally."
  // The per-PLAYER-level pickup growth lives HERE (in this every-frame rebuild) instead
  // of a one-off level-up mutation, which this very reset wiped each frame (the 21.32
  // bug — "never felt a change"). +4% pickup range per level above 1; items + percs +
  // shrines add on top. (e.g. Lv25 = +96% radius before any picks.)
  p.pickupRange = Math.max(0, ((p.level ?? 1) - 1)) * 4;
  p.xpMultiplier = 1.0;
  p.goldMultiplier = 1.0;
  p.hpRegen = 0;
  // BETA 16.36 — shield regen baseline. Items + shrines stack their flat
  // bonus on top via the +shieldRegen modifier path.
  // BETA 16.43 — Per Dylan "balance it." 1.0 → 2.0/sec so shield feels
  // like a meaningful recurring buffer (a 30-shield pool now refills in
  // ~15s vs ~30s) without making it dominant.
  p.shieldRegen = 2.0;
  p.damageBonus = 0;
  p.attackSpeedBonus = 0;
  p.thorns = 0;
  // BETA 13.77 — newly exposed; reset to 0 each frame (same pattern as the others).
  p.eliteDamageBonus = 0;
  p.projectileSpeedBonus = 0;
  p.attackAreaBonus = 0;
  // BETA 13.82 — wires `+1 Projectile Bounce` shrine (15 instances broken in cycle-3).
  p.projectileBounceBonus = 0;
  // BETA 19.70 — Dylan live: "increase difficulty buffs are broken like insanly".
  // ROOT CAUSE: `_shrineDifficultyAccumPct` was (pre-23.66: a module-level, now:
  // a per-player store) accumulator that
  // never reset per-frame. Every shrine pick added +v to it, but the per-frame
  // apply loop kept ADDING +v again each frame. After 1 minute (~3600 frames)
  // a single +8% Difficulty shrine pick produced `S._shrineDifficultyAccumPct =
  // 28,800` → spawn multiplier = 1 + 28800/100 = 289×. The "broken like
  // insanely" was literally a runaway counter. Fix: reset to 0 each frame so
  // the bonus loop below recomputes from the actual buff list, matching how
  // every other stat (armor, luck, etc.) above already resets.
  S._shrineDifficultyAccumPct = 0;
  let maxShieldTotal = 0;

  // Base stats
  // BETA 14.97 — user direction: base Max HP = 100. Reverts BETA 14.85 (150).
  // Now paired with the new enemy damage values (zombie 25 / timmy 40 /
  // heavy 60). Per-frame recompute MUST match engine.ts initGame baseline
  // or the player's HP gets silently overwritten (the bug we caught in 14.85).
  let maxHp = 100;
  let speed = 100;

  // Apply item bonuses
  if (S.itemState) {
    recalculateStats(S.itemState);
    const stats = S.itemState.playerStats;
    maxHp = stats.maxHp;
    speed = stats.moveSpeed;
    // Luck: flat points from items
    p.luck += stats.luck;
    // Pickup range: convert item system's world-unit pickup range to a % bonus
    if (stats.pickupRange > stats.basePickupRange) {
      p.pickupRange += ((stats.pickupRange / stats.basePickupRange) - 1) * 100;
    }
    // ── Wire remaining item statModifiers to player (previously unconnected) ──
    // Damage % bonus (items like Wrench +18%, Pre Workout +15%)
    if (stats.damage > stats.baseDamage) {
      p.damageBonus += ((stats.damage / stats.baseDamage) - 1) * 100;
    }
    // Attack speed % bonus (Wrench +12%, Pre Workout +12%, Not Meth +20%)
    if (stats.attackSpeed !== stats.baseAttackSpeed) {
      p.attackSpeedBonus += ((stats.attackSpeed / stats.baseAttackSpeed) - 1) * 100;
    }
    // BETA 16.91 — Adrenaline Pod (UC03) reactive combat: +12% atk
    // speed per stack while the 4s post-damage timer is in the future.
    // Re-themed in 16.91 from Stim Pod II (which dupe'd Big Ol' Boy II).
    {
      const _adrenSlot = S.itemState.inventory.find(s => s.item.id === 'UC03');
      const _adrenUntil = (state as unknown as { _adrenalineReactiveUntil?: number })._adrenalineReactiveUntil ?? 0;
      if (_adrenSlot && (state.time ?? 0) < _adrenUntil) {
        p.attackSpeedBonus += tuneOf('UC03', 'attackSpeedPct', 12) * _adrenSlot.stacks; // BETA 21.79 — editable
      }
    }
    // BETA 17.88 — item_bungula_chambers (replaces deleted N05 perc).
    // Sqrt-scaling for "extra projectiles" so stacking still rewards but
    // doesn't dominate. Plus Bungulator synergy: when item_bungulators
    // is also equipped, ALL damageBonus gets +50% (single global, not
    // per-stack on the chambers item, to keep math sane).
    // Per Dylan: "have something to do with bungulator combo ness."
    {
      const _bcSlot = S.itemState.inventory.find(s => s.item.id === 'item_bungula_chambers');
      if (_bcSlot && _bcSlot.stacks > 0) {
        const stacks = _bcSlot.stacks;
        // BETA 19.98 — was sqrt(stacks) which capped at +1 projectile MAX even
        // at 3 stacks (Math.floor(sqrt(3)) = 1). Description always said
        // "+3 extra projectiles" — now actually delivers +3 per stack flat.
        const extraProj = tuneOf('item_bungula_chambers', 'extraProjPerStack', 3) * stacks;
        p.projectileCountBonus += extraProj;
        // Synergy: Bungulators minion item equipped + Chambers stacked
        const _bungMinion = S.itemState.inventory.find(s => s.item.id === 'item_bungulators');
        if (_bungMinion && _bungMinion.stacks > 0) {
          p.damageBonus += tuneOf('item_bungula_chambers', 'synergyDmgPct', 50); // flat +damage% when the combo is live
        }
      }
    }
    // Crit chance — items add flat points (Lucky Horseshoe +8, Horseshoe II +15)
    if (stats.critChance !== stats.baseCritChance) {
      p.critChance += stats.critChance - stats.baseCritChance;
    }
    // Armor — flat (Peanut's Underwear +10, Thick Underwear +25, Wyfe Beater +15)
    p.armor += stats.armor;
    // Evasion — items store dodge as flat % (Little Black Dress +12%, Thick Underwear +15%)
    p.evasion += stats.dodgeChance;
    // HP Regen — flat (Stim Pod +5, Stim Pod II +10)
    p.hpRegen += stats.hpRegen;
    // BETA 16.36 — Shield regen — flat additive (items can grant via
    // stat: 'shieldRegen' modifiers).
    p.shieldRegen += stats.shieldRegen;
    // BETA 16.37 — Crit damage — additive % from items. PSBOT cycle-30i
    // §7D Bug A: E04 Hutch's Horseshoe III declared +20% critDamage but
    // had NO apply path. Fixed: stats.critDamage now sums flat+percent
    // additively in recalculateStats (since player.critDamage is itself
    // a % stat with base 0), and we add it here.
    p.critDamage += stats.critDamage;
    // XP multiplier (XP Juice +15%, Big Brain +10%)
    if (stats.xpGain > stats.baseXpGain) {
      p.xpMultiplier += (stats.xpGain / stats.baseXpGain) - 1; // BETA 20.66 — additive (see xpBonus note)
    }
    // BETA 19.98 — Attack range (C01 Hutch's Sausage Fingers +15%). Was dead
    // because no consumer read stats.attackRange. % bump on base 50wu maps to
    // flat p.rangeBonus (which weaponAdapter:336 reads as bonusRange).
    if (stats.attackRange > stats.baseAttackRange) {
      p.rangeBonus += (stats.attackRange - stats.baseAttackRange);
    }
    // BETA 19.98 — Gold multiplier (item_write_that_down +15% gold). Aggregated
    // via the new 'gold' stat key (bunchaBullshiiiiSystem.ts:602).
    if (stats.gold && stats.gold !== 1.0) {
      p.goldMultiplier *= stats.gold;
    }
  }

  // Apply perc bonuses
  if (S.percState) {
    const bonuses = calculatePercRunBonuses(S.percState);
    const boosted = applyPercBonusesToStats({
      baseMaxHp: maxHp,
      baseDamage: 10,
      baseMoveSpeed: speed,
      baseAttackSpeed: 1.0,
      baseCritChance: 5,
      baseDodgeChance: 0,
      baseHpRegen: 0,
      baseXpGain: 1.0,
      baseAttackRange: 200,
      basePickupRange: 200,         // BETA 13.73: 150 → 200. 77-run audit: stationary AOE builds (chad/sniper/rpk) leave 22-29% of gold drops on the ground. Bot stays in center, enemies die at perimeter, gold orbs despawn before the bot loops back. fireball_kite (kiting build = lots of motion) hits exactly 15g/kill expected; the others hit 11g/kill. 200wu range closes the gap without making the game trivial.
    }, bonuses);
    maxHp = boosted.baseMaxHp;
    speed = boosted.baseMoveSpeed;
    // Lady Luck perk adds flat luck points
    p.luck += bonuses.luck ?? 0;
    // BETA 14.46 — REMOVED `* 100` multipliers. The OLD comment claimed
    // `calculatePercRunBonuses` returns fractions (0.05 for 5%), but it
    // actually returns INTEGER PERCENTS (5 for 5%) because perc.bonusPerLevel
    // is stored as integer percent in peanutPercs.ts (e.g., 1.5 = +1.5%/level).
    // The `* 100` was making perc bonuses 100× too large — root cause of
    // user's "felt invincible / damage in the thousands" complaint. CSV
    // evidence: finalDamagePct=13220, finalAttackSpeedPct=7592 in
    // weaponlab_session_1777602166692.csv. Now player fields receive correct
    // integer % from perc bonuses (e.g., +9% damage from a level-6 +1.5%/level
    // perc). xpMultiplier / goldMultiplier divided by 100 to convert to
    // fraction-form multiplier.
    p.damageBonus      += bonuses.damage;
    p.attackSpeedBonus += bonuses.attackSpeed;
    p.critChance       += bonuses.critChance;
    p.knockbackBonus   += bonuses.knockback;
    p.hpRegen          += bonuses.hpRegen;       // flat, no conversion
    maxShieldTotal     += bonuses.shield;         // flat, no conversion
    if (bonuses.xpBonus > 0)   p.xpMultiplier  *= (1 + bonuses.xpBonus / 100);
    if (bonuses.goldBonus > 0) p.goldMultiplier *= (1 + bonuses.goldBonus / 100);
    // BETA 13.78 evasion/projectileSpeed/attackArea wires — same fix.
    p.evasion              += bonuses.dodgeChance;
    p.projectileSpeedBonus += bonuses.projectileSpeed;
    // BETA 17.77 — CRITICAL SCALING BUG FIX. The 15.51 comment below claimed
    // bonuses.attackSize was a FRACTION (0.025 per pick) needing × 100 to
    // become percent. WRONG. T13 Not Viagra is defined as
    //   `bonusPerLevel: 2.5, isPercent: true`
    // and peanutPercSystem accumulates it directly: lvl 6 = 6 × 2.5 = 15.
    // That's already a percent. The `× 100` was making it 1500% — 100× the
    // intended attack area buff. Lab live-test (50-stack max build) showed
    // Size +1500% from just T13 lvl 6. Every Not Viagra pick has been
    // giving 100× the buff written on the card since BETA 15.51.
    // FIX: drop the × 100 conversion. Bonuses.attackSize is already percent.
    p.attackAreaBonus      += bonuses.attackSize;

    // ── BETA 15.18 — Wire the 11 new-perk stat domains to the player. ──
    // ROOT CAUSE: per cycle-15 PSBOT analysis, 5 stat-anchor N-perks (N05/N07/
    // N08/N09/N14) showed final<Stat>=0 across all 50 runs even when picked.
    // Investigation: the OLD apply block above only covers original T-perk
    // stats. The NEW apply path (BETA 13.103 advanced eval, ~line 1095) has
    // a `hasNewFields` gate that skips perks without secondaryStat/condition/
    // scaling/procEffect — and NONE of N01-N14 have those fields, so the
    // gate skips ALL 14 new perks. Their bonuses got calculated into the
    // `bonuses` object but never reached the player. The "6 working perks"
    // PSBOT identified only showed >0 because shrines/items independently
    // add to those stats; the perks themselves were silently broken too.
    // Fix: add the missing applies here in the OLD path. New perks now flow
    // properly. The NEW path's hasNewFields gate is preserved as future-proof
    // scaffolding for complex perks.
    p.lifesteal              += bonuses.lifesteal;
    p.armor                  += bonuses.armor;
    p.thorns                 += bonuses.thorns;
    p.critDamage             += bonuses.critDamage;
    p.projectileCountBonus   += bonuses.projectileCount;
    p.eliteDamageBonus       += bonuses.eliteDamage;
    p.projectileBounceBonus  += bonuses.projectileBounce;
    // pickupRange is in % per applyAllBonusesToPlayer convention (lines 1019-1021).
    p.pickupRange            += bonuses.pickupRange;
    // Lazy player fields (not on Player type yet — pending types.ts cleanup):
    {
      const pAny = p as unknown as Record<string, number>;
      pAny.overheal              = bonuses.overheal;
      pAny.projectilePierceBonus = bonuses.projectilePierce;
    }
    // Overheal expands the HP pool. Engine reads finalOverheal as (maxHp - 100).
    maxHp += bonuses.overheal;
    // phaseChance is set on contactPhaseChance below (combined with NEW-path
    // procEffect total, see ~line 1214) so do NOT set it here — it gets
    // overwritten by the NEW path otherwise.

    // ── BETA 13.103 — Advanced perc evaluation (trade-off / conditional / scaling) ──
    // The existing calculatePercRunBonuses() handles vanilla single-stat percs.
    // The new pack adds 14 percs that evaluate per-frame state. We loop the
    // slots here and apply primary bonus (gated by condition/scaling) plus the
    // secondary trade-off penalty.
    {
      const enemies = state.enemies ?? [];
      const time = state.time ?? 0;
      // Pre-compute helpers used by multiple percs
      let enemiesWithin150 = 0;
      let enemiesWithin300 = 0;
      for (const e of enemies) {
        if (!e.alive) continue;
        const dx = e.x - p.x; const dy = e.y - p.y;
        const distSq = dx * dx + dy * dy;
        if (distSq <= 150 * 150) enemiesWithin150++;
        if (distSq <= 300 * 300) enemiesWithin300++;
      }
      const movingDiagonal = Math.abs(p.vx ?? 0) > 0.3 && Math.abs(p.vy ?? 0) > 0.3;
      const missingHpPct = Math.max(0, 1 - (p.hp / Math.max(1, p.maxHp)));
      const minutesSurvived = time / 60;
      const goldHeldPer100 = Math.floor((p.gold ?? 0) / 100);
      const goldSpentPer500 = Math.floor((state._runGoldSpent ?? 0) / 500);
      const recentlyDamagedTimer = state._postDamageTimer ?? 0;

      let totalNotAcidPhaseChance = 0;

      for (const slot of S.percState.slots) {
        if (!slot.perc || slot.level <= 0) continue;
        const perc = slot.perc;
        const lvl = slot.level;
        // Skip vanilla percs (no new schema fields set)
        const hasNewFields = !!(perc.secondaryStat || perc.condition || perc.scaling || perc.procEffect);
        if (!hasNewFields) continue;

        // ── Evaluate primary condition gate ──
        let conditionMet = true;
        if (perc.condition) {
          const c = perc.condition;
          if (c.type === 'enemies_within') {
            const range = c.range;
            const minCount = c.minCount;
            const count = range === 150 ? enemiesWithin150 : range === 300 ? enemiesWithin300 : (() => {
              let n = 0;
              for (const e of enemies) {
                if (!e.alive) continue;
                const dx = e.x - p.x, dy = e.y - p.y;
                if (dx * dx + dy * dy <= range * range) n++;
              }
              return n;
            })();
            conditionMet = count >= minCount;
          } else if (c.type === 'no_enemies_within') {
            const cnt = c.range === 300 ? enemiesWithin300 : (() => {
              let n = 0;
              for (const e of enemies) {
                if (!e.alive) continue;
                const dx = e.x - p.x, dy = e.y - p.y;
                if (dx * dx + dy * dy <= c.range * c.range) n++;
              }
              return n;
            })();
            conditionMet = cnt === 0;
          } else if (c.type === 'movement_diagonal') {
            conditionMet = movingDiagonal;
          } else if (c.type === 'recently_damaged') {
            conditionMet = recentlyDamagedTimer > 0;
          } else if (c.type === 'first_n_seconds') {
            conditionMet = time < c.seconds;
          } else if (c.type === 'after_n_seconds') {
            conditionMet = time >= c.seconds;
          } else if (c.type === 'enemy_subtype') {
            // Subtype gate is evaluated in the damage application path (per-target),
            // not here. Treat as false at the global stat layer.
            conditionMet = false;
          }
        }

        // ── Evaluate scaling factor (if any) ──
        let scaleFactor = 1.0;
        if (perc.scaling) {
          if (perc.scaling.type === 'missing_hp_pct') scaleFactor = missingHpPct;
          else if (perc.scaling.type === 'minutes_survived') scaleFactor = minutesSurvived;
          else if (perc.scaling.type === 'gold_held_per_100') scaleFactor = goldHeldPer100;
          else if (perc.scaling.type === 'gold_spent_per_500') scaleFactor = goldSpentPer500;
        }

        // ── Apply primary bonus when gate is met ──
        if (conditionMet) {
          const primaryAmt = perc.bonusPerLevel * lvl * scaleFactor;
          _applyPercStatBonus(p, perc.stat, primaryAmt, perc.isPercent);
          // Puberty Blockers special case: stat='damage' anchors UI but bonus
          // applies to a basket of stats (per spec). Spread to relevant stats.
          if (perc.id === 'N07') {
            _applyPercStatBonus(p, 'attackSpeed', primaryAmt, true);
            _applyPercStatBonus(p, 'maxHp',       primaryAmt, true);
            _applyPercStatBonus(p, 'moveSpeed',   primaryAmt, true);
            _applyPercStatBonus(p, 'shield',      primaryAmt, true);
          }
        }

        // ── Apply secondary stat (trade-off — paired with primary OR secondaryCondition) ──
        if (perc.secondaryStat && perc.secondaryBonusPerLevel != null) {
          let secondaryGate = true;
          if (perc.secondaryCondition) {
            const sc = perc.secondaryCondition;
            if (sc.type === 'first_n_seconds') secondaryGate = time < sc.seconds;
            else if (sc.type === 'after_n_seconds') secondaryGate = time >= sc.seconds;
            // Other types not applicable to trade-off pairs in this pack
          }
          if (secondaryGate) {
            const secAmt = perc.secondaryBonusPerLevel * lvl;
            _applyPercStatBonus(p, perc.secondaryStat, secAmt, !!perc.secondaryIsPercent);
          }
        }

        // ── Probabilistic: accumulate Not Acid phase chance ──
        if (perc.procEffect === 'phase_contact_damage' && perc.procChance) {
          totalNotAcidPhaseChance += perc.procChance * lvl;
        }
      }

      // Stash the Not Acid phase chance on player for the damage path to roll.
      // (Damage handler in engine.ts reads p.contactPhaseChance per contact event.)
      // BETA 15.18 — combine bonuses.phaseChance (from N14 picks via OLD path)
      // with totalNotAcidPhaseChance (procEffect-style, future-proof). bonuses
      // is in % (e.g., 5 = 5%), contactPhaseChance is fraction (0-0.95) — divide
      // by 100. Without this combine, my new OLD-path overheal/lifesteal/etc.
      // applies still left N14 broken because line 1214 overwrote with 0.
      p.contactPhaseChance = Math.min(
        0.95,
        (bonuses.phaseChance ?? 0) / 100 + totalNotAcidPhaseChance,
      );
    }
    // ── End BETA 13.103 advanced perc evaluation ──

    // ── BETA 14.04 — Mystery Meat Stew accumulated bonuses ──
    // Each kill rolled in engine.ts's on-kill hook accumulates into
    // state._mysteryMeatBonuses ({ maxHp, damageBonus, luck }). Apply each
    // frame so the run-permanent boosts compound.
    if (state._mysteryMeatBonuses) {
      maxHp += state._mysteryMeatBonuses.maxHp;
      p.damageBonus += state._mysteryMeatBonuses.damageBonus;
      p.luck += state._mysteryMeatBonuses.luck;
    }
  }

  // Apply shrine buffs — speed bonuses collected additively, applied once after loop
  let totalShrineSpeedPct = 0;
  // BETA 16.91 — accumulate maxShield % multiplier shrines, applied at end
  let totalShieldMultPct = 0;
  for (const buff of S.shrineAppliedBuffs) {
    const v = buff.value;
    const pct = buff.pct;
    switch (buff.stat) {
      case 'moveSpeed':
        // Accumulate instead of compounding (old: speed *= 1.08 per shrine → teleporting)
        totalShrineSpeedPct += pct ? v : 0;
        break;
      case 'maxHp':                maxHp = pct ? maxHp * (1 + v / 100) : maxHp + v; break;
      case 'damageBonus':          p.damageBonus += v; break;
      case 'attackSpeedBonus':     p.attackSpeedBonus += v; break;
      case 'luck':                 p.luck += v; break;
      case 'critChance':           p.critChance += v; break;
      case 'critDamage':           p.critDamage += v; break;
      case 'lifesteal':            p.lifesteal += v; break;
      case 'maxShield':            maxShieldTotal += v; break;
      // BETA 16.91 — % multiplier on top of flat maxShield. Accumulated
      // additively across stacked shrines; applied once after the loop
      // so it multiplies the FINAL flat-shield total.
      // BETA 17.06 — PSBOT cycle-30x BAIT CARD BUG CONFIRMED IN CYCLE-30Y.
      // Per Dylan's LeBron run (cycle-30y): picked shield_mult_20 at t=21.1s,
      // finalShield: 0 at run end. Math: if base maxShield = 0 (no flat
      // shield items picked), then `0 × 1.20 = 0`. Card promises shield,
      // delivers nothing. Fix: pair the % multiplier with a flat fallback
      // = half the percentage value (+10 / +25). Now the card grants
      // SOMETHING even on builds without flat shield, and stacks
      // multiplicatively on builds that have it.
      case 'maxShieldMult':
        totalShieldMultPct += pct ? v : 0;
        maxShieldTotal += Math.round(v * 0.5);
        break;
      // BETA 16.36 — shrine shield regen buff. Flat additive to base 1.0/sec.
      case 'shieldRegen':          p.shieldRegen += v; break;
      case 'armor':                p.armor += v; break;
      case 'evasion':              p.evasion += v; break;
      case 'knockbackBonus':       p.knockbackBonus += v; break;
      case 'projectileCountBonus': p.projectileCountBonus += v; break;
      case 'rangeBonus':           p.rangeBonus += v; break;
      case 'pickupRange':          p.pickupRange += v; break;
      case 'xpMultiplier':         p.xpMultiplier += v / 100; break; // BETA 20.66 — additive
      case 'goldMultiplier':       p.goldMultiplier *= (1 + v / 100); break;
      case 'hpRegen':              p.hpRegen += v; break;
      case 'thorns':               p.thorns += v; break;
      // BETA 13.77 — newly wired. Shrine pool's '+20% Elite Damage' now lands on
      // player.eliteDamageBonus (was 'eliteDamage' with no apply case = silent no-op).
      // attackAreaBonus / projectileSpeedBonus reserved for future shrines/items.
      case 'eliteDamageBonus':     p.eliteDamageBonus += v; break;
      case 'projectileSpeedBonus': p.projectileSpeedBonus += v; break;
      case 'attackAreaBonus':      p.attackAreaBonus += v; break;
      // BETA 13.82 — `+1 Projectile Bounce` shrine; matches SHRINE_BUFF_POOL.proj_bnc.stat = 'projectileBounces'.
      case 'projectileBounces':    p.projectileBounceBonus += v; break;
      // BETA 16.33 — wire shrine `+8% Difficulty` (was a silent no-op).
      // Dylan: "increasing difficulty means adding more enemies. btw. not
      // harder enemies." Sums into _shrineDifficultyPct (module-level
      // accumulator below) which engine.ts reads each tick and folds
      // into waveState.difficultyMultiplier. ONLY scales spawn rate
      // (waveSystem.ts:473) — never enemy stats/speed.
      case 'difficulty':
        S._shrineDifficultyAccumPct += pct ? v : 0;
        break;
      // BETA 21.18 — dash shrine buffs (Dylan: "small + simple. regenerate faster,
      // longer dashing."). engine.ts reads these at the dash recharge + duration sites.
      case 'dashRecharge':         p.dashRechargeBonus = (p.dashRechargeBonus ?? 0) + v; break;
      case 'dashDuration':         p.dashDurationBonus = (p.dashDurationBonus ?? 0) + v; break;
      default: break;
    }
  }

  // BETA 16.91 — Apply accumulated maxShield % multiplier to the flat
  // shield total. Multiplies the result of all flat-shield items +
  // shrines, so shield-tank builds can scale into late-run.
  if (totalShieldMultPct > 0) {
    maxShieldTotal = Math.round(maxShieldTotal * (1 + totalShieldMultPct / 100));
  }

  // BETA 16.96 — Generic playerStat-aware buffDimension applier.
  // PSBOT cycle-30e Phase 2 long tail: ~27 stat-modifier items
  // couldn't use the dimensional system because the apply pipeline
  // only read pickedDimensions in custom per-item code (Whoopie /
  // SHIT / etc.). Now: any BuffDimension with a `playerStat` field
  // declared in the item def adds bonusPerStack × picked-count to
  // the named player stat automatically. No per-item engine code.
  // Mechanic dims (radius / cooldown / etc.) still go through their
  // existing custom paths — playerStat is undefined there.
  if (S.itemState) {
    for (const slot of S.itemState.inventory) {
      const dims = slot.item.buffDimensions;
      if (!dims || dims.length === 0) continue;
      const picks = slot.pickedDimensions ?? {};
      for (const dim of dims) {
        if (!dim.playerStat) continue;
        const count = picks[dim.statField] ?? 0;
        if (count <= 0) continue;
        const delta = dim.bonusPerStack * count;
        switch (dim.playerStat) {
          case 'maxHp':                 maxHp += delta; break;
          case 'moveSpeed':             speed += delta; break;
          case 'rangeBonus':            p.rangeBonus += delta; break;
          case 'pickupRange':           p.pickupRange += delta; break;
          case 'damageBonus':           p.damageBonus += delta; break;
          case 'attackSpeedBonus':      p.attackSpeedBonus += delta; break;
          case 'critChance':            p.critChance += delta; break;
          case 'critDamage':            p.critDamage += delta; break;
          case 'lifesteal':             p.lifesteal += delta; break;
          case 'armor':                 p.armor += delta; break;
          case 'evasion':               p.evasion += delta; break;
          case 'thorns':                p.thorns += delta; break;
          case 'luck':                  p.luck += delta; break;
          case 'hpRegen':               p.hpRegen += delta; break;
          case 'shieldRegen':           p.shieldRegen += delta; break;
          case 'eliteDamageBonus':      p.eliteDamageBonus += delta; break;
          case 'projectileSpeedBonus':  p.projectileSpeedBonus += delta; break;
          case 'attackAreaBonus':       p.attackAreaBonus += delta; break;
          case 'projectileBounceBonus': p.projectileBounceBonus += delta; break;
          case 'projectileCountBonus':  p.projectileCountBonus += delta; break;
          case 'knockbackBonus':        p.knockbackBonus += delta; break;
          case 'xpMultiplier':          p.xpMultiplier += delta / 100; break; // BETA 20.66 — additive
          case 'goldMultiplier':        p.goldMultiplier *= (1 + delta / 100); break;
          case 'maxShieldBonus':        maxShieldTotal += delta; break;
          // BETA 17.04 — damageReduction lives on S.itemState.playerStats
          // (not directly on Player). Adds the per-pick percent to the
          // accumulated DR, capped 80% to mirror the recalculateStats
          // legacy path. enemyAdapter (16.96) reads this and applies
          // the reduction after armor in the damage pipeline.
          case 'damageReduction': {
            if (S.itemState && S.itemState.playerStats) {
              const _cur = S.itemState.playerStats.damageReduction ?? 0;
              S.itemState.playerStats.damageReduction = Math.min(80, _cur + delta);
            }
            break;
          }
        }
      }
    }
  }

  // Apply shrine speed bonus once (additive from all shrines combined) then cap.
  //
  // BETA 16.27 — PSBOT cycle-30b Bug 3 FIX. Per Dylan: "Shrine picked +8
  // moveSpeed, finalMoveSpeedPct stayed at 200%." Root cause: comment
  // said "base is 100" but `stats.moveSpeed` (line 1201) defaults to 200
  // per BullshiiiiSystem (baseMoveSpeed: 200). With base=200 and cap=200,
  // ANY positive shrine bonus pushes speed to 200+ which immediately
  // gets clipped back to 200 — net zero observable effect.
  //
  // Fix: cap relative to the base (2× base = 400) so shrine + perc
  // moveSpeed bonuses can stack into observable territory. Per Dylan's
  // "no caps" philosophy this could be removed entirely; keeping a 2×
  // ceiling for now to prevent runaway-fast bots from breaking enemy AI
  // chase math.
  //
  // BETA 16.55 — PSBOT cycle-30m. Dylan: "i was getting more speed boost
  // for running. was it working or is there a cap? ... there should be
  // no cap on stat abilities." Confirmed cap was clipping shrine bonuses
  // past +100%. Cap removed entirely. If enemy AI chase breaks at very
  // high player speeds, fix the chase math instead of capping the player.
  speed = speed * (1 + totalShrineSpeedPct / 100);
  // (Math.min cap removed — was Math.min(speed, 400). See BETA 16.55 note above.)

  // ─── Passive item effects (applied AFTER all base stats, so they can read p.armor etc.) ──
  if (S.itemState) {
    for (const slot of S.itemState.inventory) {
      const { item, stacks } = slot;

      // R01 Championship Belt: flat damage = 5% of maxHp per stack
      // Expressed as % bonus assuming a ~20 reference weapon damage
      if (item.id === 'R01') {
        const flatDmg = maxHp * tuneOf('R01', 'hpDamageFrac', 0.05) * stacks;
        p.damageBonus += (flatDmg / tuneOf('R01', 'refWeaponDamage', 20)) * 100;
      }

      // R03 Wyfe Beater: thorns = 50% of total armor per stack
      // BETA 17.36 — cycle-30aq audit Finding #9: unbounded at high armor.
      // 3×R03 + 2×R12 + N02 = 166 armor → 83 thorns/hit with R10 25%
      // AoE explode (Smashley) = stand-still farming. Cap each stack's
      // contribution at 50 thorns max so unlimited armor scaling can't
      // become unlimited thorns. Three stacks of R03 still cap at +150
      // (vs +250+ pre-cap with same gear).
      if (item.id === 'R03') {
        const perStack = Math.min(p.armor * tuneOf('R03', 'thornsArmorFrac', 0.5), tuneOf('R03', 'thornsCapPerStack', 50));
        p.thorns += perStack * stacks;
      }

      // R05 Blood Bag: +lifesteal% per stack
      if (item.id === 'R05') {
        p.lifesteal += tuneOf('R05', 'lifestealPctPerStack', 3) * stacks;
      }

      // R13 Last Resort: +30% damage when below 25% HP per stack
      if (item.id === 'R13' && state.player.hp > 0 && state.player.maxHp > 0) {
        const _inLowHp = state.player.hp / state.player.maxHp < tuneOf('R13', 'hpThreshold', 0.25);
        if (_inLowHp) {
          p.damageBonus += tuneOf('R13', 'lowHpDmgBonus', 30) * stacks;
        }
        // BETA 17.07 — PSBOT cycle-30t Tier C counter #9: Last Resort
        // activations = count of ENTRIES into the low-HP window (Dylan-
        // confirmed semantic). Sticky-flag edge-trigger so re-entering
        // (low → high → low) counts as 2, not "every frame at low".
        const _sAnyR13 = state as unknown as Record<string, number | undefined> & { _lastResortActiveLastFrame?: boolean };
        const _wasActive = _sAnyR13._lastResortActiveLastFrame === true;
        if (_inLowHp && !_wasActive) {
          _sAnyR13._lastResortActivationsThisRun = (_sAnyR13._lastResortActivationsThisRun ?? 0) + 1;
        }
        _sAnyR13._lastResortActiveLastFrame = _inLowHp;
      }

      // R15 Silly Juice: sublinear damage scaling per kill stack.
      // BETA 17.88 — Dylan ("NEUTRAL — stacks unbounded → OP risk"): replace
      // the flat +2.5%/stack curve (which was linear up to 50 stacks for
      // +125%) with a CURVED, NO-CAP scaling. Each stack contributes less
      // than the previous, so the item never runaways but always rewards.
      //
      // Formula:  damageBonus += 5 × sqrt(stacks)
      //   1 stack   → +5%       (was +2.5%)
      //   4 stacks  → +10%      (was +10%)
      //   16 stacks → +20%      (was +40%)
      //   25 stacks → +25%      (was +62.5%)
      //   50 stacks → +35%      (was +125%, the old cap)
      //   100 stacks→ +50%      (no cap — keeps stacking sublinearly)
      //   400 stacks→ +100%
      //
      // Reward at low stacks is 2× the old curve (5% vs 2.5%), so early
      // game feels MORE responsive; long runs are bounded by math, not a
      // hard cap, per Dylan's "no caps, fix scaling" direction.
      if (item.id === 'R15' && S._sillyJuiceKillStacks > 0) {
        p.damageBonus += tuneOf('R15', 'pctPerSqrtKill', 5) * Math.sqrt(S._sillyJuiceKillStacks);  // BETA 21.77 — editable per-√kill %
      }
    }
  }

  // BETA 15.26 — character passives. Layer 1 (per-level stat scaling) + Layer 2
  // stat-shaped signatures (Berserker Rage, Sponge Absorb stacks, Suppression
  // Fire stacks, Silly Juice stacks, Sniper's Eye crit damage, Wizard Surge,
  // Golden Aura radius). Event-driven Layer 2 abilities (Quick Draw, Arcane
  // Surge kill counter, Scavenger drops, King's Court bounce mult, Sponge stack
  // increment) are wired via separate hooks in enemySystem / weaponSystem.
  // This call goes LAST so passives stack on top of items + percs + shrines.
  applyCharacterPassive(state, p);

  // Apply to player
  // BETA 17.94 — Floor maxHp at 1. Dylan: "wrap around health??? no. that
  // should no be wrappin." Under STRESS-test "100 of each item" load, items
  // with negative maxHp downsides (R18 Cursed Chat -10%/stack × 100 = -1000%
  // → maxHp * -9 → negative HP cascade) were producing maxHp in the negative
  // billions. The low-HP overlay, Last Stand trigger, and death checks all
  // assume positive maxHp — negative values caused permanent screen-cover
  // orange overlay + repeat Last Stand triggers every frame.
  if (!Number.isFinite(maxHp) || maxHp < 1) maxHp = 1;
  if (p.maxHp !== maxHp) {
    const ratio = (p.maxHp > 0 && Number.isFinite(p.hp / p.maxHp)) ? p.hp / p.maxHp : 1;
    p.maxHp = maxHp;
    p.hp = Math.min(Math.round(ratio * maxHp), maxHp);
  }
  if (!Number.isFinite(p.hp) || p.hp < 0) p.hp = Math.min(p.maxHp, Math.max(0, p.hp || 0));
  p.speed = speed;
  // BETA 17.43 — cap p.evasion at 80% (matches damageReduction cap convention).
  // Was UNCAPPED — Dylan reported "bot taking 30 hits in 3 seconds and not
  // dying". Cause traced: items + percs + shrines all `+=` into p.evasion
  // with no clamp anywhere. At 100%+ evasion, the dodge check
  // `rollRng() * 100 < p.evasion` is ALWAYS TRUE → every hit dodged
  // → bot is immortal. Brotato caps dodge at 60% precisely for this reason;
  // we use 80% to match our existing damageReduction cap.
  if (p.evasion > 80) p.evasion = 80;
  // BETA 17.95 — Floor damageBonus at -99%. STRESS run showed it underflowing
  // to -3,841,704% (some negative-damage item compounded at 100 stacks). At
  // -100% or worse, weapons deal 0 or negative damage and combat math is
  // broken downstream. -99% is the harshest sane floor — player still does
  // 1% of base damage (a sting, not a kill, but the game still plays).
  // Per Dylan's "no caps" rule the UPSIDE is not capped; only the negative
  // underflow is floored. NaN/Infinity guarded the same way.
  if (!Number.isFinite(p.damageBonus) || p.damageBonus < -99) p.damageBonus = -99;
  // BETA 19.5 — Defensive: clamp p.shield to a finite number BEFORE the
  // arithmetic. If it's undefined/NaN (from a stale character init or a
  // mid-run reset that missed it), the `p.shield + (maxShieldTotal -
  // prevMaxShield)` math would produce NaN, which then poisons every
  // subsequent shield check (`(NaN ?? 0) > 0` is false → absorption
  // skipped; `NaN < maxShield` is false → regen skipped). Smoking gun
  // for the BETA 19.4 Chad run dying with "full blue bar" — maxShield=30,
  // runShieldDamageAbsorbed=0, runShieldRegenerated=0. Force shield to a
  // finite number so the bar can never be a lie.
  if (!Number.isFinite(p.shield)) p.shield = 0;
  if (!Number.isFinite(p.maxShield)) p.maxShield = 0;
  // BETA 21.42 — R12 Drac's Gas Mask grants overheal shield up to 20% of maxHp
  // (filled by hitting poisoned enemies, weaponAdapter ~436). The item floors
  // maxShield at that cap, but this per-frame recompute (`p.maxShield =
  // maxShieldTotal`) excluded R12, then the clamp below wiped the overheal —
  // so the item did NOTHING for any player without other shield sources.
  // Honor the item's own intent: include its 20%-maxHp ceiling in maxShieldTotal.
  const _r12Slot = S.itemState?.inventory.find(s => s.item.id === 'R12');
  if (_r12Slot) {
    const _r12Cap = Math.round(maxHp * tuneOf('R12', 'overhealCapPct', 0.2));
    if (maxShieldTotal < _r12Cap) maxShieldTotal = _r12Cap;
  }
  const prevMaxShield = p.maxShield;
  p.maxShield = maxShieldTotal;
  // BETA 21.60 — stash the shield %-multiplier total so the HUD hover-preview can
  // project the TRUE additive gain (% of the flat BASE, a constant per pick) instead
  // of "% of current shield" (which grew each pick and looked like compounding to
  // Dylan). The applied shield is additive — this just makes the preview honest.
  (p as { _shieldMultPct?: number })._shieldMultPct = totalShieldMultPct;
  // When shield capacity increases (new item picked up), fill the newly gained portion
  // BETA 20.00 — UN-GATED from Debt (Dylan: healing/shield keep running). Shield
  // > 0 deactivates Debt which is intentional in the new design.
  if (maxShieldTotal > prevMaxShield) {
    p.shield = Math.min(p.maxShield, p.shield + (maxShieldTotal - prevMaxShield));
  }
  if (p.shield > p.maxShield) p.shield = p.maxShield;
  // BETA 19.5 — Final defensive clamp. If anything above produced NaN
  // (shouldn't, but belt-and-suspenders), force shield finite again.
  if (!Number.isFinite(p.shield)) p.shield = 0;
}

// ─── The Adapter Module ─────────────────────────────────────────────
export const itemsAdapterModule: ItemsModule = {
  init(_state: GameState) {
    // BETA 23.66 — CO-OP PHASE 1 part 2: a new run keeps only the ACTIVE
    // player's store; leftover co-op stores from a previous run are dropped
    // (single-player: none exist, so this is a no-op). The active store is
    // then field-reset below EXACTLY as the old module-let init did — fields
    // init never touched (peak trackers, offer queues, hover indices, chest
    // session state) keep their old cross-run lifecycles unchanged.
    for (const k of Object.keys(_itemsStores) as PlayerId[]) {
      if (k !== _activeItemsPlayerId) delete _itemsStores[k];
    }
    // In bot mode, unlock all percs so the bot sees the full perc pool
    const activePercIds = _botModeUnlockAll
      ? new Set(PEANUT_PERCS.map(p => p.id))
      : new Set(getActivePercs().map(p => p.id));
    S.percState = createPercRunState(activePercIds, 4); // 4 perc slots max
    // BETA 17.15 — PSBOT cycle-30ag CRITICAL BUG. Bot mode was unlocking
    // ALL percs but NOT all items. So the bot's chest/vendor/shrine rolls
    // were filtered through `getActiveBullshiiiiItems()` which gates on
    // user's lifetimeStats-driven unlock state. 4 weeks of audits ran on
    // restricted item pools. "Never picked" findings included items the
    // bot was LOCKED out of seeing. Same fix as percs: unlock all in bot mode.
    const activeBullshiiiiIds = _botModeUnlockAll
      ? new Set(BUNCHA_BULLSHIIII.map(b => b.id))
      : new Set(getActiveBullshiiiiItems().map(b => b.id));
    // BETA 21.61 — locked base items are ALWAYS in the drop pool (anti all-legendary
    // floor). Even if a glitch or stale loadout dropped them, force them back in so
    // the rarity roller can always find commons and the luck-gate stays meaningful.
    ALWAYS_ON_BULLSHIIII_IDS.forEach(id => activeBullshiiiiIds.add(id));
    S.itemState = createBullshiiiiSystem(activeBullshiiiiIds);
    // BETA 22.05 — CROSS-RUN variety (Dylan: "each run feels the same"). REVERSES the 20.06
    // per-run wipe: we now PERSIST the cross-chest anti-dupe ring across runs ON PURPOSE, so a
    // fresh run's opening chests avoid the items the LAST run just showed. The 20.06 worry that
    // this "degrades early variety" was backwards — persisting is what makes runs feel different.
    // (Ring is small + rolls off fast, so it never starves the pool.)
    // clearRecentChestPicks();  // intentionally NOT called anymore
    S.pendingLevelUp = false;
    S.cachedLevelUpDisplay = null;
    S.levelUpSelectedIndex = -1;
    S.shrineAppliedBuffs = [];
    S._shrineDifficultyAccumPct = 0;
    // BETA 19.71 — reset cross-level-up stat upgrade dedup queue
    S._recentUpgradeWeaponIds.length = 0;
    S.pendingShrineBuffOptions = null;
    S.pendingChestItems = []; S.pendingChestDimensions = []; S.pendingChestJitters = []; S.pendingChestTiers = [];
    S._sillyJuiceKillStacks = 0;
    S._sillyJuiceVfxGains = 0;  // BETA 20.72 — VFX throttle counter resets with the stacks
  },

  update(state: GameState, _dt: number) {
    // BETA 20.72 — park the live state so the bare Silly Juice trigger
    // functions above (no state param) can reach state.particles. VFX only.
    _vfxStateRef = state;
    if (!S.percState || !S.itemState) return;

    // When engine sets phase to 'levelup', generate options if not already pending
    if (state.phase === 'levelup' && !S.pendingLevelUp) {
      // Feed XP into perc system for internal tracking
      addXp(S.percState, state.player.xpToNext, 1.0);
      // Generate level-up options (3 perc choices) — luck boosts rarity of offers
      generateLevelUpOptions(S.percState, state.player.luck);
      if (S.percState.currentOptions && S.percState.currentOptions.length > 0) {
        S.pendingLevelUp = true;
        S.levelUpSelectedIndex = -1;
        S.levelUpHoverIndex = -1;

        // Every 3 levels, offer a new weapon if player has fewer than 4
        const level = state.player.level;
        if (level > 0 && level % 3 === 0) {
          const ws = getWeaponSystemState();
          const equippedIds = new Set((ws?.weapons ?? []).map(w => w.definitionId));
          // Only offer weapons that have a full definition in WEAPON_DATABASE
          // BETA 17.15 — PSBOT cycle-30ag addendum: same bug as items. Bot
          // mode's level-up weapon offers were filtered through getActiveWeapons()
          // which reads the user's checked-in-menu set. If Dylan has only 12
          // weapons checked, the bot only ever sees 12 in level-up offers.
          // BETA 20.82 — Dylan: "same random offers for ALL weapons... not
          // have priority weapons shown." This card USED to draw from the
          // active pool MINUS your equipped weapons — so your character's
          // signature starter (auto-equipped at run start) was the ONE weapon
          // never offered. That per-character exclusion was the whole "I get
          // weapons I never see with other characters" tell. Now: draw from
          // the FULL active pool, identical for every character. An UNOWNED
          // pick → a NEW-weapon card (needs a free slot); an OWNED pick (incl.
          // your signature) → that weapon's UPGRADE card via the existing,
          // battle-tested path (handleLevelUpSelection dispatches on .type,
          // so 'weapon_upgrade' routes to levelUpWeaponById — zero new wiring,
          // no character broken since the signature STILL starts equipped).
          const weaponPool = _botModeUnlockAll ? WEAPONS : getActiveWeapons();
          const fullPool = weaponPool.filter(w => !!getWeaponDef(w.id));
          if (fullPool.length > 0) {
            // BETA 22.05 — VARIETY: when you have an open slot, PREFER an unowned weapon you
            // haven't been offered in the last few runs (cross-run anti-repeat), so this every-3
            // card surfaces fresh guns. Falls back to the full pool (owned pick → upgrade card).
            const _unowned = fullPool.filter(w => !equippedIds.has(w.id));
            let _pref = _unowned.filter(w => !_crossRunOfferedWeapons.includes(w.id));
            if (_pref.length === 0) _pref = _unowned;
            const _pickFrom = (_pref.length > 0 && (ws?.weapons.length ?? 0) < 4) ? _pref : fullPool;
            const pick = _pickFrom[Math.floor(rollRng() * _pickFrom.length)];
            const _slotIdx = S.percState.currentOptions.length - 1;
            if (equippedIds.has(pick.id)) {
              // Owned (your signature lands here) → upgrade card. Null when the
              // weapon is already max level → simply no weapon card this level.
              const _upCard = buildWeaponUpgradeCard(pick.id, _slotIdx, state.player.luck ?? 0);
              if (_upCard) S.pendingWeaponCard = { ..._upCard, optionIndex: _slotIdx };
            } else if ((ws?.weapons.length ?? 0) < 4) {
              // Unowned + a free slot → new weapon (cross-run ring remembers it).
              S.pendingWeaponCard = _makeNewWeaponCard(pick, _slotIdx);
              _noteCrossRunWeapon(pick.id);
            }
            // else: unowned but all 4 slots full → no weapon card this level.
          }
        }
      } else {
        // No options available — skip level-up screen
        state.phase = 'playing';
      }
      return;
    }
    // Don't process anything during level-up screen
    if (S.pendingLevelUp || state.phase === 'levelup') return;

    // During choice screens: still apply bonuses but skip normal logic
    if (state.phase === 'shrine_choice' || state.phase === 'chest_choice') {
      applyAllBonusesToPlayer(state);
      return;
    }

    // Apply all bonuses to player stats every frame
    applyAllBonusesToPlayer(state);
  },

  render(_ctx: CanvasRenderingContext2D, _state: GameState, _viewBounds: ViewBounds) {
    // Items don't render in world space — HUD handles display
  },

  getLevelUpChoices(_state: GameState): UpgradeChoice[] {
    if (!S.percState || !S.percState.currentOptions || S.percState.currentOptions.length === 0) return [];
    return S.percState.currentOptions.map((opt, i) => ({
      id: `perc_${i}`,
      name: opt.perc.name,
      description: opt.type === 'new_perc'
        ? `NEW: ${opt.perc.description}`
        : `Upgrade → Lv${opt.resultLevel}: ${opt.bonusDisplay}`,
      icon: opt.perc.iconUrl,
      rarity: opt.rarity,
    }));
  },

  applyUpgrade(state: GameState, upgradeId: string) {
    if (!S.percState) return;
    const index = parseInt(upgradeId.replace('perc_', ''), 10);
    if (!isNaN(index)) {
      handleLevelUpSelection(index);
      state.phase = 'playing';
    }
  },
};
